<template>
  <div id="app">
    <router-view v-if="$route.meta.public" />
    <template v-else>
    <a class="app-skip-link" href="#primaryAppContent">跳到主要内容</a>
    <button
      type="button"
      class="mobile-nav-toggle"
      :aria-expanded="mobileNavOpen ? 'true' : 'false'"
      aria-label="打开功能导航"
      @click="toggleMobileNav"
    >
      <i class="el-icon-menu"></i>
    </button>
    <button
      v-if="mobileNavOpen"
      type="button"
      class="mobile-nav-backdrop"
      aria-label="点击空白处关闭导航"
      @click="closeMobileNav"
    ></button>
    <aside class="sidebar" :class="{ 'mobile-open': mobileNavOpen, 'desktop-collapsed': desktopNavCollapsed }">
      <div class="brand-lockup">
        <div class="brand-mark">
          <img src="@/assets/yuntu-logo.jpg" alt="耘图AI同创 Logo" />
        </div>
        <div class="brand-copy">
          <strong>耘图AI无线画布</strong>
          <span>设计、类设计行业AI助手</span>
        </div>
        <button type="button" class="mobile-nav-close" aria-label="关闭功能导航" @click="closeMobileNav">
          <i class="el-icon-close"></i>
        </button>
      </div>
      <nav class="side-nav grouped-nav" aria-label="AI 功能导航">
        <router-link class="home-nav-link" active-class="active" :to="{ name: 'home' }" @click.native="closeMobileNav">
          <span class="nav-group-icon"><i class="el-icon-s-home"></i></span>
          <span class="nav-group-label">首页</span>
        </router-link>
        <router-link class="home-nav-link playground-nav-link" active-class="active" :to="{ name: 'playground' }" @click.native="closeMobileNav">
          <span class="nav-group-icon"><i class="el-icon-cpu"></i></span>
          <span class="nav-group-label">耘图对话</span>
        </router-link>
        <section
          v-for="group in navGroups"
          :key="group.key"
          class="nav-group"
          :class="{ active: isNavGroupActive(group), expanded: expandedNavGroup === group.key }"
        >
          <router-link
            v-if="group.routeName"
            class="nav-group-trigger"
            :to="{ name: group.routeName }"
            :title="group.label"
            @click.native="closeMobileNav"
          >
            <span class="nav-group-icon"><i :class="group.icon"></i></span>
            <span class="nav-group-label">{{ group.label }}</span>
          </router-link>
          <button
            v-else
            type="button"
            class="nav-group-trigger"
            :title="desktopNavCollapsed ? group.label : null"
            :aria-expanded="expandedNavGroup === group.key ? 'true' : 'false'"
            @click="toggleNavGroup(group.key)"
          >
            <span class="nav-group-icon"><i :class="group.icon"></i></span>
            <span class="nav-group-label">{{ group.label }}</span>
            <i class="el-icon-arrow-down nav-group-arrow"></i>
          </button>
          <transition name="nav-submenu">
            <div v-if="!group.routeName" v-show="expandedNavGroup === group.key" class="nav-submenu">
              <router-link
                v-for="item in group.children"
                :key="item.name"
                class="nav-subitem"
                exact
                active-class="active"
                :class="{ unavailable: item.placeholder }"
                :aria-disabled="item.placeholder ? 'true' : null"
                :to="item.placeholder ? $route.fullPath : { name: item.name }"
                :title="item.label"
                @click.native="handleNavItemClick(item)"
              >
                <span class="nav-subitem-label">{{ item.label }}</span>
              </router-link>
            </div>
          </transition>
        </section>
        <router-link class="home-nav-link cases-nav-link" active-class="active" :to="{ name: 'cases' }" @click.native="closeMobileNav">
          <span class="nav-group-icon"><i class="el-icon-files"></i></span>
          <span class="nav-group-label">案例广场</span>
        </router-link>
        <router-link class="home-nav-link materials-nav-link" active-class="active" :to="{ name: 'materials' }" @click.native="closeMobileNav">
          <span class="nav-group-icon"><i class="el-icon-collection"></i></span>
          <span class="nav-group-label">素材广场</span>
        </router-link>
        <router-link class="home-nav-link referral-nav-link" active-class="active" :to="{ name: 'referrals' }" @click.native="closeMobileNav">
          <span class="nav-group-icon"><i class="el-icon-present"></i></span>
          <span class="nav-group-label">邀请分佣</span>
        </router-link>
        <router-link class="home-nav-link feedback-nav-link" active-class="active" :to="{ name: 'feedback' }" @click.native="closeMobileNav">
          <span class="nav-group-icon"><i class="el-icon-chat-dot-round"></i></span>
          <span class="nav-group-label">我要提问</span>
        </router-link>
      </nav>
      <button
        type="button"
        class="sidebar-collapse"
        :aria-label="desktopNavCollapsed ? '展开侧边导航' : '收起侧边导航'"
        :title="desktopNavCollapsed ? '展开侧边导航' : null"
        @click="toggleDesktopNav"
      >
        <i :class="desktopNavCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'" aria-hidden="true"></i>
        <span>收起侧边栏</span>
      </button>
      <nav v-if="false" class="side-nav">
        <router-link
          class="nav-item"
          active-class="active"
          to="/create"
          title="AI创作"
          @click.native="clearCreationPreset"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24">
              <rect x="4" y="4" width="16" height="16" rx="1"/>
              <path d="M4 11h7V4M11 11v9M11 15h9M16 4v7"/>
            </svg>
          </span>
          <span class="nav-label">AI创作</span>
        </router-link>
        <router-link
          class="nav-item"
          active-class="active"
          to="/style-replace"
          title="风格替换"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h10v10H4z"/><path d="M10 10h10v10H10z"/><path d="m16 4 3 3-3 3M19 7h-5"/></svg>
          </span>
          <span class="nav-label">风格替换</span>
        </router-link>
        <router-link
          class="nav-item"
          active-class="active"
          to="/lighting-conversion"
          title="灯光转换"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3a7 7 0 1 0 7 7"/><path d="M12 3a8 8 0 0 1 8 8 7 7 0 0 1-8-8Z"/><path d="M6 19h12M8 22h8"/></svg>
          </span>
          <span class="nav-label">灯光转换</span>
        </router-link>
        <router-link
          class="nav-item"
          active-class="active"
          to="/lighting-adjustment"
          title="灯光调节"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v3M5.6 5.6l2.1 2.1M3 12h3M18 12h3M16.3 7.7l2.1-2.1"/><circle cx="12" cy="12" r="4"/><path d="M5 19h14M8 22h8"/></svg>
          </span>
          <span class="nav-label">灯光调节</span>
        </router-link>
        <router-link
          class="nav-item"
          active-class="active"
          to="/smart-erase"
          title="智能消除"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 15 7-8 6 6-7 7H7z"/><path d="m9 11 5 5M4 20h16"/><path d="M18 4v3M16.5 5.5h3"/></svg>
          </span>
          <span class="nav-label">智能消除</span>
        </router-link>
        <router-link
          class="nav-item"
          active-class="active"
          to="/object-replacement"
          title="框选替换"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 4h6M4 4v6M20 20h-6M20 20v-6"/>
              <rect x="7" y="7" width="8" height="8" rx="1"/>
              <path d="m16 5 3 3-3 3M19 8h-4"/>
            </svg>
          </span>
          <span class="nav-label">框选替换</span>
        </router-link>
        <router-link
          v-if="showWhiteModelNav"
          class="nav-item"
          active-class="active"
          to="/white-model"
          title="线稿出方案"
        >
          <span class="nav-icon white-model-nav-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m4 8 8-4 8 4-8 4z"/>
              <path d="m4 8v8l8 4 8-4V8M12 12v8"/>
              <path d="m8 10v4l4 2 4-2v-4"/>
            </svg>
          </span>
          <span class="nav-label">线稿出方案</span>
        </router-link>
        <router-link
          class="nav-item"
          active-class="active"
          to="/projects"
          title="项目管理"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24"><path d="M4 6.5h6l1.5 2H20V19H4z"/></svg>
          </span>
          <span class="nav-label">项目管理</span>
        </router-link>
        <router-link
          class="nav-item"
          active-class="active"
          to="/cases"
          title="案例库"
        >
          <span class="nav-icon">
            <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="14" rx="2"/><circle cx="9" cy="10" r="1.5"/><path d="m6 17 4-4 3 3 2-2 3 3"/></svg>
          </span>
          <span class="nav-label">案例库</span>
        </router-link>
      </nav>
    </aside>

    <main id="primaryAppContent" class="main-shell" :class="{ 'nav-collapsed': desktopNavCollapsed }" tabindex="-1">
      <header class="topbar">
        <div class="topbar-title">
          <p class="eyebrow">WintoAI</p>
          <h1>{{ currentViewTitle }}</h1>
        </div>
        <transition name="customer-guide">
          <div v-if="customerServiceGuideVisible" class="customer-service-guide" role="note" aria-label="客服使用提示">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3a8 8 0 0 0-8 8v1.5A2.5 2.5 0 0 0 6.5 15H7v-5H4.1M12 3a8 8 0 0 1 8 8v1.5a2.5 2.5 0 0 1-2.5 2.5H17v-5h2.9M17 16c-1.1 2-2.8 3-5 3" />
              <circle cx="10.8" cy="19" r="1" />
            </svg>
            <span>生成有疑问？</span>
            <button class="customer-service-guide-action" type="button" @click="openCustomerService">联系客服</button>
            <button class="customer-service-guide-close" type="button" aria-label="关闭客服提示" title="关闭" @click="dismissCustomerServiceGuide">×</button>
          </div>
        </transition>
        <div class="header-actions">
          <button
            type="button"
            class="workflow-entry"
            aria-label="进入工作流"
            title="进入工作流"
            @click="openWorkflow"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="6" cy="6" r="2" />
              <circle cx="18" cy="6" r="2" />
              <circle cx="12" cy="18" r="2" />
              <path d="M8 6h8M7 8l4 8M17 8l-4 8" />
            </svg>
            <span>工作流</span>
          </button>
          <button
            v-if="isAdmin"
            type="button"
            class="pricing-entry"
            aria-label="查看图片生成收费标准"
            title="收费标准"
            @click="pricingDialogVisible = true"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 4h14v16H5z"/>
              <path d="M8 8h8M8 12h3M14 12h2M8 16h3M14 16h2"/>
            </svg>
            <span>收费标准</span>
          </button>
          <button
            type="button"
            class="membership-pricing-entry"
            aria-label="购买会员并查看收费明细"
            title="购买会员"
            @click="membershipPricingVisible = true"
          >
            <span class="vip-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="m4 7 4.2 4L12 5l3.8 6L20 7l-1.4 10H5.4z"/><path d="M6 20h12"/></svg>
            </span>
            <span class="membership-pricing-entry__label">购买会员</span>
          </button>
          <button class="earn-points" @click="openVipCenter('points')">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="8"/>
              <path d="M9.5 8.5h5M12 8.5v7M9.5 12h5"/>
            </svg>
            <span>获取积分</span>
          </button>

          <div class="member-shortcuts">
            <button class="credits" aria-label="查看积分明细" title="查看积分明细" @click="openVipCenter('history')">
              <span class="points-coin">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="8"/>
                  <path d="M9.5 8.5h5M12 8.5v7M9.5 12h5"/>
                </svg>
              </span>
              <strong>{{ pointsBalance }}</strong>
            </button>
          </div>

          <div class="user-session">
            <span>{{ currentUser ? currentUser.displayName : '' }}</span>
            <button type="button" title="退出登录" @click="handleLogout">退出</button>
          </div>

          <el-dropdown class="language-dropdown" trigger="click">
            <button class="language-entry" aria-label="选择语言">
              <span>中文</span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 9 5 5 5-5"/></svg>
            </button>
            <el-dropdown-menu slot="dropdown" class="language-menu">
              <el-dropdown-item>简体中文</el-dropdown-item>
              <el-dropdown-item disabled>English（即将上线）</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-dropdown class="compact-account" trigger="click" @command="handleAccountCommand">
            <button class="compact-account-entry" aria-label="账户菜单">
              <i class="el-icon-user-solid"></i>
              <span>{{ currentUser ? currentUser.displayName : '账户' }}</span>
            </button>
            <el-dropdown-menu slot="dropdown" class="account-menu">
              <el-dropdown-item command="membership-pricing" icon="el-icon-medal">购买会员</el-dropdown-item>
              <el-dropdown-item command="pricing" icon="el-icon-tickets">收费标准</el-dropdown-item>
              <el-dropdown-item command="points" icon="el-icon-coin">积分与会员</el-dropdown-item>
              <el-dropdown-item command="notifications" icon="el-icon-bell">通知</el-dropdown-item>
              <el-dropdown-item divided command="logout" icon="el-icon-switch-button">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <router-view
        :key="$route.name"
        :preset="creationPreset"
        @start-design="startProjectDesign"
        @use-reference="useCaseAsReference"
        @preview-image="handlePreviewImage"
        @open-customer-service="openCustomerService"
      />
    </main>

    <membership-pricing-dialog
      :visible.sync="membershipPricingVisible"
      @choose="handleMembershipPricingAction"
      @consult="handleMembershipConsult"
    />

    <el-dialog
      title="会员与积分中心"
      :visible.sync="vipDialogVisible"
      custom-class="vip-dialog"
      width="640px"
      :show-close="false"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="purchase-center">
        <header class="purchase-header">
          <div class="purchase-brand">
            <div class="purchase-crown">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m4 7 4.2 4L12 5l3.8 6L20 7l-1.4 10H5.4z"/>
                <path d="M6 20h12"/>
              </svg>
            </div>
            <div>
              <h2>会员与积分中心</h2>
              <p>{{ isVipMember ? `${membershipPlanLabel}权益已生效，积分可按需充值` : '两种年度方案，为个人与企业提供恰好的权益' }}</p>
            </div>
          </div>
          <button class="purchase-close" aria-label="关闭" title="关闭" @click="vipDialogVisible = false">×</button>
        </header>

        <div class="purchase-steps" role="tablist" aria-label="购买类型">
          <button
            role="tab"
            :aria-selected="vipPanel === 'membership'"
            :class="{ active: vipPanel === 'membership' }"
            @click="vipPanel = 'membership'"
          >
            <span class="step-number">1</span>
            <span class="step-copy"><strong>年度会员</strong><small>{{ isVipMember ? membershipPlanLabel : '365 元起 / 年' }}</small></span>
            <span v-if="isVipMember" class="step-status">已开通</span>
          </button>
          <button
            role="tab"
            :aria-selected="vipPanel === 'points'"
            :class="{ active: vipPanel === 'points' }"
            @click="vipPanel = 'points'"
          >
            <span class="step-number">2</span>
            <span class="step-copy"><strong>积分充值</strong><small>1 元 = 10 积分</small></span>
            <svg v-if="!isVipMember" class="step-lock" viewBox="0 0 24 24" aria-label="需先开通VIP">
              <rect x="6" y="10" width="12" height="10" rx="2"/>
              <path d="M9 10V7a3 3 0 0 1 6 0v3"/>
            </svg>
          </button>
          <button
            role="tab"
            :aria-selected="vipPanel === 'history'"
            :class="{ active: vipPanel === 'history' }"
            @click="openVipCenter('history')"
          >
            <span class="step-number">3</span>
            <span class="step-copy"><strong>积分明细</strong><small>查看收入与消耗</small></span>
          </button>
        </div>

        <section v-if="vipPanel === 'membership'" class="purchase-panel membership-panel" role="tabpanel">
          <div class="limited-offer" role="status">
            <span class="offer-pulse" aria-hidden="true"></span>
            <strong>选择适合你的年度会员 · 支付成功后立即生效</strong>
            <button type="button" class="membership-consult-compact" @click="openCustomerService">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 13.5v-2a7.5 7.5 0 0 1 15 0v2"/><path d="M4.5 12.5H3.8A1.8 1.8 0 0 0 2 14.3v2.4a1.8 1.8 0 0 0 1.8 1.8h1.7v-6ZM19.5 12.5h.7a1.8 1.8 0 0 1 1.8 1.8v2.4a1.8 1.8 0 0 1-1.8 1.8h-1.7v-6Z"/></svg>
              咨询客服
            </button>
          </div>
          <div v-if="isVipMember" class="current-membership-strip" role="status">
            <span>当前权益</span>
            <strong>{{ membershipPlanLabel }}</strong>
            <small>{{ membershipPlan === 'ENTERPRISE' ? '全部功能已解锁' : '已解锁耘图对话' }} · {{ vipExpiryShortText }}</small>
          </div>
          <div class="membership-plan-grid" role="radiogroup" aria-label="选择会员套餐">
            <button type="button" class="membership-plan-card personal" :class="{ selected: selectedMembershipPlan === 'PERSONAL', current: membershipPlan === 'PERSONAL', bound: membershipPlan === 'PERSONAL' }" role="radio" :aria-checked="selectedMembershipPlan === 'PERSONAL'" :disabled="membershipPlan === 'ENTERPRISE'" @click="selectedMembershipPlan = 'PERSONAL'">
              <span class="plan-card-label">{{ membershipPlan === 'PERSONAL' ? '当前权益 · 已绑定' : '个人专享 · 仅对话' }}</span>
              <h3>个人会员</h3>
              <p>让 AI 成为你随时在线的设计顾问</p>
              <div class="plan-card-price"><span>¥</span><strong>365</strong><small>/年</small></div>
              <ul>
                <li><strong>设计难题随时问</strong>，快速获得专业思路</li>
                <li>上传户型或效果图，获得空间分析建议</li>
                <li>获取风格、材质、配色与软装灵感</li>
                <li><strong>开通即赠 500 积分</strong>，耘图对话内可直接使用</li>
              </ul>
              <b class="plan-select-state">{{ membershipPlan === 'PERSONAL' ? '当前权益 · 已绑定' : (selectedMembershipPlan === 'PERSONAL' ? '已选择' : '选择个人版') }}</b>
            </button>
            <button type="button" class="membership-plan-card enterprise" :class="{ selected: selectedMembershipPlan === 'ENTERPRISE', current: membershipPlan === 'ENTERPRISE', upgrade: membershipPlan === 'PERSONAL' }" role="radio" :aria-checked="selectedMembershipPlan === 'ENTERPRISE'" @click="selectedMembershipPlan = 'ENTERPRISE'">
              <span class="best-value-tag">{{ membershipPlan === 'PERSONAL' ? '推荐升级 · 解锁全部功能' : '团队首选 · 物超所值' }}</span>
              <span class="plan-card-label">{{ membershipPlan === 'ENTERPRISE' ? '当前套餐 · 团队版权益' : (membershipPlan === 'PERSONAL' ? '升级企业会员 · 团队版权益' : '企业会员 · 团队版权益') }}</span>
              <h3>企业会员</h3>
              <p>从沟通灵感到效果图交付，一站式提效</p>
              <div class="plan-card-price"><span>¥</span><strong>1980</strong><small>/年</small></div>
              <div class="enterprise-value"><strong>平均仅 ¥165 / 月</strong><span>一次解锁全站创作能力</span></div>
              <ul>
                <li><strong>团队版全功能权益</strong>，包含个人会员全部能力</li>
                <li><strong>数百种设计玩法与工作流组合</strong></li>
                <li>AI 效果图、局部改造、换材质等全部创作</li>
                <li><strong>开通即赠 500 积分</strong>，到账后即可使用</li>
                <li><strong>365 天全年有效</strong>，支持高频创作与商业提案</li>
                <li><strong>1v1 专属售后客服</strong>，使用问题快速响应</li>
              </ul>
              <b class="plan-select-state">{{ selectedMembershipPlan === 'ENTERPRISE' ? (membershipPlan === 'PERSONAL' ? '已选择升级企业会员' : '已选择') : (membershipPlan === 'PERSONAL' ? '升级企业会员' : '选择企业版') }}</b>
            </button>
          </div>
          <div v-if="membershipPlan !== selectedMembershipPlan" class="membership-actions">
            <el-button
              class="purchase-primary membership-action"
              type="primary"
              :disabled="pointsLoading"
              @click="confirmVipPurchase"
            >
              <img class="payment-method-icon" src="/payment-icons/wechat.png" alt="" />
              {{ pointsLoading ? '正在创建微信支付订单…' : membershipPurchaseButtonText }}
            </el-button>
          </div>
          <p class="purchase-reassurance">支付成功后自动开通 · 有效期 1 年 · 积分统一按 ¥1 = 10 积分充值</p>
          <div v-if="isVipMember" class="vip-validity-card" role="status" aria-label="年度会员有效期">
            <div class="validity-header">
              <div class="validity-title">
                <span class="validity-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3v3M17 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"/><path d="m8 14 2.5 2.5L16 11"/></svg>
                </span>
                <div><strong>{{ membershipPlanLabel }}</strong><small>会员权益已生效</small></div>
              </div>
              <b><i aria-hidden="true"></i>有效期内</b>
            </div>
            <div class="validity-period">
              <div>
                <span>开通时间</span>
                <strong>{{ formatVipDate(vipActivatedAt, true) || '-' }}</strong>
              </div>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M14 7l5 5-5 5"/></svg>
              <div>
                <span>到期时间</span>
                <strong>{{ formatVipDate(vipExpiresAt, true) || '长期有效' }}</strong>
              </div>
            </div>
          </div>
          <button v-if="isVipMember" class="continue-points" @click="vipPanel = 'points'">继续购买积分 →</button>
        </section>

        <section v-else-if="vipPanel === 'points'" class="purchase-panel points-panel" role="tabpanel">
          <div v-if="!isVipMember" class="points-locked">
            <div class="locked-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="5" y="10" width="14" height="10" rx="2"/>
                <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10"/>
              </svg>
            </div>
            <div>
              <span class="locked-step">需要先完成第 1 步</span>
              <strong>积分充值尚未解锁</strong>
              <p>开通任一年度会员后，即可选择积分套餐并完成充值。</p>
            </div>
            <el-button class="unlock-vip-button" type="primary" size="small" @click="vipPanel = 'membership'">查看两种会员方案</el-button>
          </div>

          <div v-else class="member-ready">
            <span>VIP</span>
            <div><strong>{{ membershipPlanLabel }}已开通</strong><small>积分充值权限已解锁</small></div>
          </div>

          <div class="points-exchange">
            <div>
              <span>充值比例</span>
              <strong>¥1 = 10 积分</strong>
            </div>
            <div>
              <span>图片消耗</span>
              <strong>中配 {{ mediumGenerationPoints }} / 高配 {{ highGenerationPoints }} 积分</strong>
              <small>按用户主动选择的生成质量计费</small>
            </div>
          </div>

          <div class="points-section-title">
            <div>
              <strong>选择充值积分</strong>
              <small>{{ isVipMember ? '选择适合本次使用量的套餐' : '以下套餐将在开通 VIP 后解锁' }}</small>
            </div>
            <span v-if="!isVipMember">暂未解锁</span>
          </div>

          <div class="points-package-grid" :class="{ locked: !isVipMember }">
            <button
              v-for="item in pointsPackages"
              :key="item.id"
              :disabled="!isVipMember"
              :class="{ selected: isVipMember && selectedPointPackageId === item.id }"
              @click="selectedPointPackageId = item.id"
            >
              <span v-if="item.recommended" class="recommended-tag">推荐</span>
              <span v-if="isVipMember && selectedPointPackageId === item.id" class="package-selected">✓</span>
              <small>{{ item.label }}</small>
              <strong>{{ item.points }}<em>积分</em></strong>
              <b>¥{{ item.price }}</b>
              <span>约生成 {{ item.images }} 张</span>
            </button>
          </div>

          <template v-if="isVipMember">
            <div class="points-summary">
              <span>本次充值</span>
              <strong>{{ selectedPointPackage.points }} 积分</strong>
              <b>应付 ¥{{ selectedPointPackage.price }}</b>
            </div>

            <el-button
              class="purchase-primary points-action"
              type="primary"
              :loading="pointsLoading"
              @click="confirmPointsPurchase"
            >
              <img class="payment-method-icon" src="/payment-icons/wechat.png" alt="" />
              微信支付充值 · ¥{{ selectedPointPackage.price }}
            </el-button>
          </template>
        </section>
        <section v-else class="purchase-panel points-history-panel" role="tabpanel">
          <div class="points-history-summary">
            <div><span>当前可用积分</span><strong>{{ pointsBalance }}</strong></div>
            <button type="button" @click="vipPanel = isVipMember ? 'points' : 'membership'">获取积分</button>
          </div>
          <div v-if="pointsHistoryLoading" class="points-history-empty"><i class="el-icon-loading"></i><span>正在加载积分明细</span></div>
          <div v-else-if="!pointsTransactions.length" class="points-history-empty"><i class="el-icon-coin"></i><span>暂无积分收支记录</span></div>
          <div v-else class="points-history-list">
            <article v-for="item in pointsTransactions" :key="item.id">
              <span class="history-icon" :class="{ expense: item.amount < 0 }"><i :class="item.amount < 0 ? 'el-icon-minus' : 'el-icon-plus'"></i></span>
              <div><strong>{{ item.description }}</strong><small>{{ formatPointsTime(item.createdAt) }} · 余额 {{ item.balanceAfter }}</small></div>
              <b :class="{ expense: item.amount < 0 }">{{ item.amount > 0 ? '+' : '' }}{{ item.amount }}</b>
            </article>
          </div>
        </section>
      </div>
    </el-dialog>

    <el-dialog
      title="微信支付"
      :visible.sync="wechatPayDialogVisible"
      custom-class="wechat-pay-dialog"
      width="380px"
      append-to-body
      :close-on-click-modal="false"
      @closed="stopWechatPaymentPolling"
    >
      <template slot="title"><span class="payment-dialog-title"><img src="/payment-icons/wechat.png" alt="" />微信支付</span></template>
      <div class="wechat-pay-content">
        <div class="wechat-pay-mark"><img src="/payment-icons/wechat.png" alt="微信支付" /></div>
        <h3>{{ wechatPayProductType === 'POINTS_RECHARGE' ? '微信扫码购买积分' : `微信扫码开通${wechatPayMembershipLabel}` }}</h3>
        <p v-if="wechatPayProductType === 'POINTS_RECHARGE'">
          应付 <strong>¥{{ formatPaymentAmount(wechatPayAmountYuan) }}</strong>，支付成功到账 {{ wechatPayPointsAmount }} 积分
        </p>
        <p v-else>应付 <strong>¥{{ formatPaymentAmount(wechatPayAmountYuan) }}</strong>，开通即赠 500 积分</p>
        <div class="wechat-pay-qr">
          <img v-if="wechatPayQrDataUrl" :src="wechatPayQrDataUrl" alt="微信扫码支付二维码" />
          <i v-else class="el-icon-loading"></i>
        </div>
        <span class="wechat-pay-status" :class="{ success: wechatPayStatus === 'FULFILLED' }">
          {{ wechatPayStatusText }}
        </span>
        <small>请在 {{ formatPaymentExpiry(wechatPayExpiresAt) }} 前完成支付</small>
      </div>
    </el-dialog>

    <pricing-dialog v-if="isAdmin" :visible.sync="pricingDialogVisible" />
    <transition name="viewer">
      <div
        v-if="imageViewer.open"
        ref="imageViewer"
        class="image-viewer"
        :class="{ 'has-navigation': viewerHasMultipleImages }"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        :aria-label="imageViewer.title"
        @click.self="closeImageViewer"
        @keydown.esc.stop.prevent="closeImageViewer"
        @keydown.left.prevent="showPreviousImage"
        @keydown.right.prevent="showNextImage"
      >
        <div class="viewer-topbar">
          <div>
            <strong>{{ imageViewer.title }}</strong>
            <span>{{ viewerPositionText }} · 图片已完整适应屏幕，方向键可切换</span>
          </div>
          <button
            class="viewer-download"
            title="下载当前图片（PNG）"
            aria-label="下载当前图片（PNG）"
            @click="downloadViewerImage"
          ><i class="el-icon-download"></i><span>下载 PNG</span></button>
          <button class="viewer-close" title="关闭大图" aria-label="关闭大图" @click="closeImageViewer">×</button>
        </div>
        <button
          v-if="viewerHasMultipleImages"
          class="viewer-nav viewer-prev"
          title="上一张（←）"
          aria-label="上一张"
          @click="showPreviousImage"
        >
          ‹
        </button>
        <button
          v-if="viewerHasMultipleImages"
          class="viewer-nav viewer-next"
          title="下一张（→）"
          aria-label="下一张"
          @click="showNextImage"
        >
          ›
        </button>
        <div
          ref="viewerStage"
          class="viewer-stage"
          :class="{ dragging: viewerDragging }"
          @wheel.prevent="handleViewerWheel"
          @pointerdown="startImageDrag"
          @pointermove="moveImageDrag"
          @pointerup="endImageDrag"
          @pointercancel="endImageDrag"
        >
          <div class="viewer-canvas" :style="viewerCanvasStyle">
            <img
              :src="$ossImageUrl(imageViewer.src, 'large')"
              :alt="imageViewer.title"
              draggable="false"
              @load="resetViewerPosition()"
            />
          </div>
        </div>
        <div class="viewer-toolbar" aria-label="图片缩放工具">
          <button title="缩小" aria-label="缩小" :disabled="viewerScale <= 1" @click="zoomImage(-0.25)">−</button>
          <span>{{ Math.round(viewerScale * 100) }}%</span>
          <button title="放大" aria-label="放大" :disabled="viewerScale >= 4" @click="zoomImage(0.25)">+</button>
          <button class="viewer-reset" title="完整适应屏幕" @click="resetImageZoom">适应</button>
        </div>
      </div>
    </transition>
    </template>

    <button
      type="button"
      class="customer-service-fab"
      :class="{ 'is-loading': customerServiceLoading, 'is-open': customerServiceOpen }"
      :aria-expanded="customerServiceOpen ? 'true' : 'false'"
      aria-controls="customer-service-panel"
      aria-label="联系在线客服"
      title="有问题？联系在线客服"
      @click="openCustomerService"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4.5 13.5v-2a7.5 7.5 0 0 1 15 0v2" />
        <path d="M4.5 12.5H3.8A1.8 1.8 0 0 0 2 14.3v2.4a1.8 1.8 0 0 0 1.8 1.8h1.7v-6Z" />
        <path d="M19.5 12.5h.7a1.8 1.8 0 0 1 1.8 1.8v2.4a1.8 1.8 0 0 1-1.8 1.8h-1.7v-6Z" />
        <path d="M19 18.5c-1.2 1.5-3.1 2.3-5.5 2.3" />
        <circle cx="12.3" cy="20.8" r="1.2" />
      </svg>
      <span class="customer-service-label">客服</span>
      <span class="customer-service-status" aria-hidden="true"></span>
    </button>

    <transition name="customer-service-panel">
      <section
        v-if="customerServiceOpen"
        id="customer-service-panel"
        class="customer-service-panel"
        role="dialog"
        aria-modal="false"
        aria-label="在线客服"
      >
        <header>
          <div>
            <span class="customer-service-avatar" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M5 13v-2a7 7 0 0 1 14 0v2M5 12H4a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h1v-6ZM19 12h1a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-1v-6Z" />
              </svg>
            </span>
            <span><strong>在线客服</strong><small>53 客服平台</small></span>
          </div>
          <button type="button" aria-label="关闭在线客服" title="关闭" @click="closeCustomerService">×</button>
        </header>
        <iframe
          :src="customerServiceUrl"
          title="53 在线客服"
          allow="clipboard-write"
          referrerpolicy="strict-origin-when-cross-origin"
        ></iframe>
      </section>
    </transition>

  </div>
</template>

<script>
import { authState, logout } from '@/services/auth'
import { apiRequest } from '@/services/api'
import { downloadGeneratedImageDirectly, downloadImageAsPng } from '@/utils/imageDownload'
import { generationPoints } from '@/utils/outputQuality'
import { requireWorkflowAppUrl } from '@/utils/workflowAppUrl'
import MembershipPricingDialog from '@/components/MembershipPricingDialog.vue'
import PricingDialog from '@/components/PricingDialog.vue'
import QRCode from 'qrcode'

const navGroups = Object.freeze([
  {
    key: 'selectionReplacement',
    label: '框选替换',
    icon: 'el-icon-crop',
    children: [
      { name: 'selectionReplacement', label: '框选替换' },
      { name: 'lightingFixtureReplacement', label: '换灯具' },
      { name: 'curtainReplacement', label: '换窗帘' },
      { name: 'furnitureReplacement', label: '换家具' },
      { name: 'backgroundReplacement', label: '换背景' },
      { name: 'floorReplacement', label: '换地面' },
      { name: 'ceilingReplacement', label: '换吊顶' },
      { name: 'doorWindowReplacement', label: '换门窗' },
      { name: 'cabinetReplacement', label: '换柜子' }
    ]
  },
  {
    key: 'aspectRatioExpansion',
    label: '原图裁切',
    icon: 'el-icon-scissors',
    routeName: 'aspectRatioExpansion'
  },
  {
    key: 'editing',
    label: '智能改图',
    icon: 'el-icon-picture-outline',
    children: [
      { name: 'oneClickCutout', label: '一键抠图' },
      { name: 'styleReplace', label: '风格替换' },
      { name: 'highResolution', label: '高清放大' },
      { name: 'smartErase', label: '智能消除' }
    ]
  },
  {
    key: 'lighting',
    label: '智能灯光',
    icon: 'el-icon-sunny',
    children: [
      { name: 'lightingConversion', label: '灯光转换' },
      { name: 'lightingAdjustment', label: '灯光调节' },
      { name: 'whiteModel', label: '黑底线稿' }
    ]
  },
  {
    key: 'softDecoration',
    label: '智能软装',
    icon: 'el-icon-house',
    children: [
      { name: 'softDecorationExtract', label: '软装方案提取' },
      { name: 'furnitureCutoutRender', label: '家具白底渲染' },
      { name: 'furnitureSceneGeneration', label: '家具环境生成' }
    ]
  },
  {
    key: 'creation',
    label: '平面创意',
    icon: 'el-icon-magic-stick',
    children: [
      { name: 'create', label: '平面转效果图' },
      { name: 'lineDraftPlan', label: '线稿出方案' }
    ]
  },
])

export default {
  name: 'App',
  components: { MembershipPricingDialog, PricingDialog },
  data() {
    return {
      navGroups,
      mobileNavOpen: false,
      desktopNavCollapsed: false,
      expandedNavGroup: '',
      showWhiteModelNav: false,
      creationPreset: null,
      pricingDialogVisible: false,
      membershipPricingVisible: false,
      vipDialogVisible: false,
      vipPanel: 'membership',
      isVipMember: false,
      membershipPlan: 'NONE',
      selectedMembershipPlan: 'ENTERPRISE',
      vipActivatedAt: '',
      vipExpiresAt: '',
      offerRemainingSeconds: 0,
      offerCountdownTimer: null,
      pointsBalance: 0,
      pointsLoading: false,
      wechatPayDialogVisible: false,
      wechatPayOrderNo: '',
      wechatPayProductType: '',
      wechatPayAmountYuan: 0,
      wechatPayPointsAmount: 0,
      wechatPayQrDataUrl: '',
      wechatPayStatus: '',
      wechatPayExpiresAt: '',
      wechatPayPollingTimer: null,
      insufficientAlertVisible: false,
      pointsHistoryLoading: false,
      pointsTransactions: [],
      notificationUnread: true,
      customerServiceLoading: true,
      customerServiceOpen: false,
      customerServiceGuideVisible: true,
      customerServiceUrl: '',
      selectedPointPackageId: 'standard',
      pointsPackages: [
        { id: 'starter', label: '轻量体验', points: 100, price: 10 },
        { id: 'standard', label: '日常使用', points: 300, price: 30, recommended: true },
        { id: 'pro', label: '高频创作', points: 500, price: 50 },
        { id: 'team', label: '团队储备', points: 1000, price: 100 }
      ].map(item => ({ ...item, images: Math.floor(item.points / generationPoints('medium')) })),
      viewerScale: 1,
      viewerReturnFocus: null,
      viewerDragging: false,
      viewerPointerId: null,
      viewerDragStartX: 0,
      viewerDragStartY: 0,
      viewerDragScrollLeft: 0,
      viewerDragScrollTop: 0,
      imageViewer: {
        open: false,
        src: '',
        title: '',
        items: [],
        index: 0,
        fastDownloadTaskId: ''
      }
    }
  },
  computed: {
    mediumGenerationPoints() { return generationPoints('medium') },
    highGenerationPoints() { return generationPoints('high') },
    currentUser() {
      return authState.user
    },
    isAdmin() {
      return Boolean(this.currentUser) && String(this.currentUser.role).toUpperCase() === 'ADMIN'
    },
    currentView() {
      return this.$route.name || 'create'
    },
    currentViewTitle() {
      return this.$route.meta.title || '耘图AI创作'
    },
    selectedPointPackage() {
      const fallbackPackage = { points: 0, price: 0 }
      return this.pointsPackages.find(item => item.id === this.selectedPointPackageId)
        || this.pointsPackages[0]
        || fallbackPackage
    },
    membershipPlanLabel() {
      return this.membershipPlan === 'ENTERPRISE' ? '企业会员' : this.membershipPlan === 'PERSONAL' ? '个人会员' : '普通用户'
    },
    membershipPurchaseButtonText() {
      const upgrading = this.membershipPlan === 'PERSONAL' && this.selectedMembershipPlan === 'ENTERPRISE'
      return this.selectedMembershipPlan === 'ENTERPRISE'
        ? `${upgrading ? '立即升级' : '立即开通企业会员'} · 微信支付 ¥1980`
        : '立即开通个人会员 · 微信支付 ¥365'
    },
    wechatPayMembershipLabel() {
      return this.wechatPayProductType === 'ENTERPRISE_ANNUAL' ? '企业会员' : '个人会员'
    },
    wechatPayStatusText() {
      const labels = {
        CREATING: '正在创建订单',
        PENDING: '等待扫码支付',
        FULFILLED: this.wechatPayProductType === 'POINTS_RECHARGE' ? '支付成功，积分已到账' : '支付成功，会员已开通',
        EXPIRED: '二维码已过期，请重新下单',
        FAILED: '支付订单创建失败'
      }
      return labels[this.wechatPayStatus] || '等待扫码支付'
    },
    offerCountdown() {
      const pad = value => String(value).padStart(2, '0')
      const remaining = Math.max(0, this.offerRemainingSeconds)
      return {
        hours: pad(Math.floor(remaining / 3600)),
        minutes: pad(Math.floor((remaining % 3600) / 60)),
        seconds: pad(remaining % 60)
      }
    },
    vipExpiryShortText() {
      const expiry = this.formatVipDate(this.vipExpiresAt, false)
      return expiry ? `有效期至 ${expiry}` : '会员权益已生效'
    },
    vipRemainingText() {
      if (!this.vipExpiresAt) return '长期有效'
      const expiresAt = new Date(this.vipExpiresAt).getTime()
      if (Number.isNaN(expiresAt)) return '会员权益已生效'
      const remainingDays = Math.max(0, Math.ceil((expiresAt - Date.now()) / 86400000))
      return `剩余 ${remainingDays} 天`
    },
    viewerHasMultipleImages() {
      return this.imageViewer.items.length > 1
    },
    viewerPositionText() {
      const count = this.imageViewer.items.length
      return count > 1 ? `${this.imageViewer.index + 1} / ${count}` : '1 / 1'
    },
    viewerCanvasStyle() {
      const size = `${this.viewerScale * 100}%`
      return { width: size, height: size }
    }
  },
  watch: {
    currentUser(user) {
      if (user) this.$nextTick(() => this.loadPointsWallet())
      else {
        this.pointsBalance = 0
        this.isVipMember = false
        this.membershipPlan = 'NONE'
        this.vipActivatedAt = ''
        this.vipExpiresAt = ''
      }
    },
    mobileNavOpen(open) {
      document.body.classList.toggle('mobile-nav-open', open)
    },
    '$route.name': {
      immediate: true,
      handler() {
        const activeGroup = this.navGroups.find(group => this.isNavGroupActive(group))
        if (activeGroup) this.expandedNavGroup = activeGroup.routeName ? '' : activeGroup.key
        this.closeMobileNav()
      }
    }
  },
  mounted() {
    try {
      this.desktopNavCollapsed = window.localStorage.getItem('yuntu-main-sidebar-collapsed') === '1'
    } catch (error) {
      this.desktopNavCollapsed = false
    }
    try {
      this.customerServiceGuideVisible = window.sessionStorage.getItem('customerServiceGuideDismissed') !== '1'
    } catch (error) {
      this.customerServiceGuideVisible = true
    }
    window.addEventListener('resize', this.handleViewportResize)
    document.addEventListener('keydown', this.handleGlobalKeydown)
    this.updateOfferCountdown()
    this.offerCountdownTimer = window.setInterval(this.updateOfferCountdown, 1000)
    this.loadCustomerService()
    window.addEventListener('points-updated', this.loadPointsWallet)
    window.addEventListener('insufficient-points', this.handleInsufficientPoints)
    this.loadPointsWallet()
  },
  beforeDestroy() {
    document.body.classList.remove('viewer-open')
    document.body.classList.remove('mobile-nav-open')
    window.removeEventListener('resize', this.handleViewportResize)
    window.removeEventListener('points-updated', this.loadPointsWallet)
    window.removeEventListener('insufficient-points', this.handleInsufficientPoints)
    document.removeEventListener('keydown', this.handleGlobalKeydown)
    window.clearInterval(this.offerCountdownTimer)
    this.stopWechatPaymentPolling()
  },
  methods: {
    openWorkflow() {
      try {
        const target = new URL(requireWorkflowAppUrl())
        window.open(target.toString(), '_blank', 'noopener,noreferrer')
      } catch (error) {
        this.showToast(error.message || '工作流地址未正确配置')
      }
    },
    dismissCustomerServiceGuide() {
      this.customerServiceGuideVisible = false
      try {
        window.sessionStorage.setItem('customerServiceGuideDismissed', '1')
      } catch (error) {
        // 无痕或禁用存储时，本次页面生命周期内仍保持关闭。
      }
    },
    async loadPointsWallet() {
      if (!this.currentUser || this.pointsLoading) return
      this.pointsLoading = true
      try {
        const wallet = await apiRequest('/api/points', { retries: 0 })
        this.pointsBalance = Number(wallet.pointsBalance || 0)
        this.isVipMember = Boolean(wallet.vipEnabled)
        this.membershipPlan = wallet.membershipPlan || 'NONE'
        this.selectedMembershipPlan = this.membershipPlan === 'PERSONAL' ? 'PERSONAL' : 'ENTERPRISE'
        this.vipActivatedAt = wallet.vipActivatedAt || ''
        this.vipExpiresAt = wallet.vipExpiresAt || ''
      } catch (error) {
        if (error.status !== 401) this.showToast(error.message || '积分信息加载失败')
      } finally {
        this.pointsLoading = false
      }
    },
    async handleInsufficientPoints(event) {
      if (this.insufficientAlertVisible) return
      this.insufficientAlertVisible = true
      await this.loadPointsWallet()
      const detail = event && event.detail ? event.detail : {}
      const enterpriseRequired = detail.code === 'ENTERPRISE_MEMBERSHIP_REQUIRED'
      const vipRequired = detail.code === 'VIP_REQUIRED' || enterpriseRequired
      const required = Number(detail.required || 0)
      const message = vipRequired
        ? (enterpriseRequired ? '当前功能仅限企业会员。升级后即可解锁全部 AI 设计功能。' : '会员未开通或已到期。请先选择适合的年度会员方案。')
        : required > 1
        ? `当前积分余额为 ${this.pointsBalance}，本次功能需要 ${required} 积分。请先获取积分后再使用。`
        : `当前积分余额为 ${this.pointsBalance}，积分为 0 时无法使用平台功能。请先获取积分后再使用。`
      try {
        await this.$alert(message, enterpriseRequired ? '需要企业会员' : (vipRequired ? '需要年度会员' : '积分不足'), {
          confirmButtonText: enterpriseRequired ? '查看企业会员' : (vipRequired ? '查看会员方案' : (this.isVipMember ? '充值积分' : '开通会员')),
          type: 'warning',
          customClass: 'points-insufficient-alert',
          center: true,
          closeOnClickModal: false,
          closeOnPressEscape: true
        })
        this.openVipCenter(vipRequired ? 'membership' : (this.isVipMember ? 'points' : 'membership'))
      } catch (error) {
        // 用户主动关闭提示时保持在当前页面。
      } finally {
        this.insufficientAlertVisible = false
      }
    },
    loadCustomerService() {
      if (typeof window.show_mobile_chat === 'function') {
        this.customerServiceLoading = false
        return
      }

      window.acc_chattype = 3
      let script = document.getElementById('customer-service-53kf')
      const handleReady = () => {
        this.customerServiceLoading = false
      }
      const handleError = () => {
        this.customerServiceLoading = false
      }

      if (!script) {
        script = document.createElement('script')
        script.id = 'customer-service-53kf'
        script.src = 'https://tb.53kf.com/code/code/6363ea6d6607d786609f73e74d4ab9ad3/1'
        script.async = true
        script.charset = 'utf-8'
        script.addEventListener('load', handleReady, { once: true })
        script.addEventListener('error', handleError, { once: true })
        document.head.appendChild(script)
        return
      }

      script.addEventListener('load', handleReady, { once: true })
      script.addEventListener('error', handleError, { once: true })
    },
    openCustomerService() {
      if (typeof window.show_mobile_chat === 'function') {
        window.show_mobile_chat()
        return
      }

      if (!this.customerServiceUrl) {
        const params = new URLSearchParams({
          kf_sign: 'TM3NTMTc4Ng4MTE4MDgwMDMyNTMyMDAzNzMxNDk5NjE=',
          arg: '11149961',
          style: '1',
          kflist: 'off',
          zdkf_type: '1',
          language: 'cn',
          charset: 'GBK',
          tpl: 'minichat2',
          interface_color: '1',
          interface_theme: '1',
          minchat_style: '1',
          referer: window.location.href,
          talktitle: document.title
        })
        this.customerServiceUrl = `https://www18.53kf.com/webCompany.php?${params.toString()}`
      }
      this.customerServiceOpen = true
    },
    closeCustomerService() {
      this.customerServiceOpen = false
    },
    updateOfferCountdown() {
      const now = new Date()
      const endOfDay = new Date(now)
      endOfDay.setHours(24, 0, 0, 0)
      this.offerRemainingSeconds = Math.max(0, Math.floor((endOfDay.getTime() - now.getTime()) / 1000))
    },
    toggleMobileNav() {
      this.mobileNavOpen = !this.mobileNavOpen
    },
    toggleDesktopNav() {
      this.desktopNavCollapsed = !this.desktopNavCollapsed
      try {
        window.localStorage.setItem('yuntu-main-sidebar-collapsed', this.desktopNavCollapsed ? '1' : '0')
      } catch (error) {
        // 禁用本地存储时，本次页面生命周期内仍保持用户选择。
      }
    },
    closeMobileNav() {
      this.mobileNavOpen = false
    },
    handleViewportResize() {
      if (window.innerWidth > 767 && this.mobileNavOpen) this.closeMobileNav()
    },
    handleGlobalKeydown(event) {
      if (event.key === 'Escape' && this.mobileNavOpen) this.closeMobileNav()
    },
    toggleNavGroup(groupKey) {
      this.expandedNavGroup = this.expandedNavGroup === groupKey ? '' : groupKey
    },
    handleNavItemClick(item) {
      if (item.placeholder) {
        this.$message.info('暂未开放')
        return
      }
      if (item.name === 'create') this.clearCreationPreset()
      if (window.innerWidth <= 1279) {
        this.$nextTick(() => {
          const activeElement = document.activeElement
          if (activeElement && typeof activeElement.blur === 'function') activeElement.blur()
        })
      }
      if (window.innerWidth <= 767) {
        this.$nextTick(() => {
          this.expandedNavGroup = ''
          this.closeMobileNav()
        })
      }
    },
    handleAccountCommand(command) {
      if (command === 'membership-pricing') {
        this.membershipPricingVisible = true
        return
      }
      if (command === 'pricing') {
        this.pricingDialogVisible = true
        return
      }
      if (command === 'logout') {
        this.handleLogout()
        return
      }
      if (command === 'notifications') {
        this.openNotifications()
        return
      }
      this.openVipCenter('points')
    },
    isNavGroupActive(group) {
      if (group.routeName) return group.routeName === this.$route.name
      return group.children.some(item => item.name === this.$route.name)
    },
    async handleLogout() {
      await logout()
      await this.$router.replace({ name: 'login' })
    },
    clearCreationPreset() {
      this.creationPreset = null
    },
    openCreationWithPreset(type, data) {
      this.creationPreset = { type, data, id: `${type}-${Date.now()}` }
      const navigate = this.currentView === 'create'
        ? Promise.resolve()
        : this.$router.push({ name: 'create' })
      navigate.then(() => {
        this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
      })
    },
    startProjectDesign(project) {
      this.openCreationWithPreset('project', project)
    },
    useCaseAsReference(caseItem) {
      this.openCreationWithPreset('case', caseItem)
    },
    handlePreviewImage(image, context) {
      const title = typeof context === 'string'
        ? context
        : context && context.title
          ? context.title
          : `${context.name} · ${context.style}`
      const items = context && typeof context === 'object' && Array.isArray(context.items)
        ? context.items
        : []
      const index = context && typeof context === 'object' ? context.index : null
      this.openImageViewer(image, title, items, index)
    },
    normalizeViewerItems(items, image, title) {
      const normalized = (Array.isArray(items) ? items : []).map((item, index) => {
        if (typeof item === 'string') return { src: item, title: `${title} · ${index + 1}` }
        return {
          src: item && (item.src || item.url),
          title: item && item.title ? item.title : `${title} · ${index + 1}`,
          key: item && item.key ? item.key : '',
          fastDownloadTaskId: item && item.fastDownloadTaskId ? item.fastDownloadTaskId : ''
        }
      }).filter(item => item.src)
      if (!normalized.length) return [{ src: image, title }]
      if (!normalized.some(item => item.src === image)) normalized.push({ src: image, title })
      return normalized
    },
    openImageViewer(image, title, items = [], selectedIndex = null) {
      this.viewerReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
      const normalizedItems = this.normalizeViewerItems(items, image, title)
      const matchedIndex = Number.isInteger(selectedIndex) && selectedIndex >= 0 && selectedIndex < normalizedItems.length
        ? selectedIndex
        : normalizedItems.findIndex(item => item.src === image && item.title === title)
      const index = matchedIndex >= 0 ? matchedIndex : Math.max(0, normalizedItems.findIndex(item => item.src === image))
      const selected = normalizedItems[index] || { src: image, title }
      this.viewerScale = 1
      this.viewerDragging = false
      this.viewerPointerId = null
      this.imageViewer = {
        open: true,
        src: selected.src,
        title: selected.title,
        items: normalizedItems,
        index,
        fastDownloadTaskId: selected.fastDownloadTaskId || ''
      }
      document.body.classList.add('viewer-open')
      this.$nextTick(() => {
        if (this.$refs.imageViewer) this.$refs.imageViewer.focus()
        this.resetViewerPosition()
      })
    },
    closeImageViewer() {
      const returnFocus = this.viewerReturnFocus
      this.endImageDrag()
      this.imageViewer = { open: false, src: '', title: '', items: [], index: 0, fastDownloadTaskId: '' }
      this.viewerScale = 1
      this.viewerReturnFocus = null
      document.body.classList.remove('viewer-open')
      this.$nextTick(() => {
        if (returnFocus && document.contains(returnFocus) && typeof returnFocus.focus === 'function') returnFocus.focus()
      })
    },
    setViewerImage(index) {
      const items = this.imageViewer.items
      if (!items.length) return
      const normalizedIndex = (index + items.length) % items.length
      const selected = items[normalizedIndex]
      this.endImageDrag()
      this.viewerScale = 1
      this.imageViewer = {
        ...this.imageViewer,
        src: selected.src,
        title: selected.title,
        index: normalizedIndex,
        fastDownloadTaskId: selected.fastDownloadTaskId || ''
      }
      this.resetViewerPosition()
    },
    showPreviousImage() {
      if (this.viewerHasMultipleImages) this.setViewerImage(this.imageViewer.index - 1)
    },
    showNextImage() {
      if (this.viewerHasMultipleImages) this.setViewerImage(this.imageViewer.index + 1)
    },
    zoomImage(delta) {
      this.viewerScale = Math.min(4, Math.max(1, Number((this.viewerScale + delta).toFixed(2))))
      this.resetViewerPosition(true)
    },
    resetImageZoom() {
      this.viewerScale = 1
      this.resetViewerPosition()
    },
    handleViewerWheel(event) {
      this.zoomImage(event.deltaY < 0 ? 0.25 : -0.25)
    },
    startImageDrag(event) {
      if (event.target.tagName !== 'IMG') return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      const stage = this.$refs.viewerStage
      if (!stage) return
      this.viewerDragging = true
      this.viewerPointerId = event.pointerId
      this.viewerDragStartX = event.clientX
      this.viewerDragStartY = event.clientY
      this.viewerDragScrollLeft = stage.scrollLeft
      this.viewerDragScrollTop = stage.scrollTop
      if (stage.setPointerCapture) stage.setPointerCapture(event.pointerId)
      event.preventDefault()
    },
    moveImageDrag(event) {
      if (!this.viewerDragging || event.pointerId !== this.viewerPointerId) return
      const stage = this.$refs.viewerStage
      if (!stage) return
      stage.scrollLeft = this.viewerDragScrollLeft - (event.clientX - this.viewerDragStartX)
      stage.scrollTop = this.viewerDragScrollTop - (event.clientY - this.viewerDragStartY)
      event.preventDefault()
    },
    endImageDrag(event) {
      if (event && this.viewerPointerId !== null && event.pointerId !== this.viewerPointerId) return
      const stage = this.$refs.viewerStage
      const pointerId = this.viewerPointerId
      if (stage && pointerId !== null && stage.hasPointerCapture && stage.hasPointerCapture(pointerId)) {
        stage.releasePointerCapture(pointerId)
      }
      this.viewerDragging = false
      this.viewerPointerId = null
    },
    resetViewerPosition(center = false) {
      this.$nextTick(() => {
        const stage = this.$refs.viewerStage
        if (!stage) return
        stage.scrollLeft = center ? Math.max(0, (stage.scrollWidth - stage.clientWidth) / 2) : 0
        stage.scrollTop = center ? Math.max(0, (stage.scrollHeight - stage.clientHeight) / 2) : 0
      })
    },
    async downloadViewerImage() {
      if (!this.imageViewer.src) return
      try {
        if (this.imageViewer.fastDownloadTaskId) {
          downloadGeneratedImageDirectly(
            this.imageViewer.fastDownloadTaskId,
            this.imageViewer.title || 'AI生成图片'
          )
          this.$message({ message: 'PNG 图片已开始高速下载', type: 'success', showClose: true, duration: 2200, offset: 24 })
          return
        }
        await downloadImageAsPng(this.imageViewer.src, this.imageViewer.title || 'AI生成图片')
        this.$message({ message: 'PNG 图片已开始下载', type: 'success', showClose: true, duration: 2200, offset: 24 })
      } catch (error) {
        this.$message({
          message: error.message || '图片下载失败，请稍后重试',
          type: 'error',
          showClose: true,
          duration: 2800,
          offset: 24
        })
      }
    },
    showToast(message) {
      this.$message({ message, showClose: true, duration: 2200, offset: 24 })
    },
    handleMembershipPricingAction(plan) {
      this.handleMembershipConsult(plan)
    },
    handleMembershipConsult() {
      this.membershipPricingVisible = false
      this.openCustomerService()
    },
    openVipCenter(panel = 'membership') {
      this.vipPanel = panel
      if (panel === 'membership') this.selectedMembershipPlan = this.membershipPlan === 'PERSONAL' ? 'PERSONAL' : 'ENTERPRISE'
      this.vipDialogVisible = true
      if (panel === 'history') this.loadPointsTransactions()
    },
    async loadPointsTransactions() {
      if (this.pointsHistoryLoading) return
      this.pointsHistoryLoading = true
      try {
        const rows = await apiRequest('/api/points/transactions', { retries: 0 })
        this.pointsTransactions = Array.isArray(rows) ? rows : []
      } catch (error) {
        this.showToast(error.message || '积分明细加载失败')
      } finally {
        this.pointsHistoryLoading = false
      }
    },
    formatPointsTime(value) {
      return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-'
    },
    formatVipDate(value, includeTime = true) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      const options = includeTime
        ? { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }
        : { year: 'numeric', month: '2-digit', day: '2-digit' }
      return date.toLocaleString('zh-CN', options).replace(/\//g, '-')
    },
    openNotifications() {
      const message = this.notificationUnread ? '通知已标记为已读' : '暂无新通知'
      this.notificationUnread = false
      this.showToast(message)
    },
    async confirmVipPurchase() {
      if (this.pointsLoading) return
      this.pointsLoading = true
      try {
        const order = await apiRequest('/api/payments/wechat/membership/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json;charset=UTF-8' },
          body: JSON.stringify({ plan: this.selectedMembershipPlan })
        })
        await this.showWechatPaymentOrder(order)
      } catch (error) {
        this.showToast(error.message || '会员开通失败')
      } finally {
        this.pointsLoading = false
      }
    },
    async showWechatPaymentOrder(order) {
      this.wechatPayOrderNo = order.orderNo
      this.wechatPayProductType = order.productType || ''
      this.wechatPayAmountYuan = Number(order.amountYuan || 0)
      this.wechatPayPointsAmount = Number(order.pointsAmount || 0)
      this.wechatPayStatus = order.status
      this.wechatPayExpiresAt = order.expiresAt
      this.wechatPayQrDataUrl = await QRCode.toDataURL(order.codeUrl, {
        width: 220,
        margin: 1,
        color: { dark: '#17211c', light: '#ffffff' }
      })
      this.wechatPayDialogVisible = true
      this.startWechatPaymentPolling()
    },
    startWechatPaymentPolling() {
      this.stopWechatPaymentPolling()
      this.wechatPayPollingTimer = window.setInterval(this.pollWechatPayment, 2500)
      this.pollWechatPayment()
    },
    stopWechatPaymentPolling() {
      if (this.wechatPayPollingTimer) window.clearInterval(this.wechatPayPollingTimer)
      this.wechatPayPollingTimer = null
    },
    async pollWechatPayment() {
      if (!this.wechatPayOrderNo || !this.wechatPayDialogVisible) return
      try {
        const order = await apiRequest(`/api/payments/wechat/orders/${encodeURIComponent(this.wechatPayOrderNo)}`, { retries: 0 })
        this.wechatPayStatus = order.status
        if (order.status === 'FULFILLED') {
          this.stopWechatPaymentPolling()
          await this.loadPointsWallet()
          if (order.productType === 'POINTS_RECHARGE') {
            this.vipPanel = 'points'
            this.showToast(`微信支付成功，${Number(order.pointsAmount || this.wechatPayPointsAmount)} 积分已到账`)
          } else {
            this.vipPanel = 'points'
            this.showToast(`微信支付成功，${order.productType === 'ENTERPRISE_ANNUAL' ? '企业会员' : '个人会员'}已开通`)
          }
          window.setTimeout(() => { this.wechatPayDialogVisible = false }, 800)
        } else if (['EXPIRED', 'FAILED', 'CLOSED'].includes(order.status)) {
          this.stopWechatPaymentPolling()
        }
      } catch (error) {
        if (error.status === 401) this.stopWechatPaymentPolling()
      }
    },
    formatPaymentExpiry(value) {
      if (!value) return '--:--'
      return new Date(value).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
    },
    formatPaymentAmount(value) {
      return Number(value || 0).toFixed(2)
    },
    async confirmPointsPurchase() {
      if (!this.isVipMember) {
        this.vipPanel = 'membership'
        this.showToast('请先开通年度会员')
        return
      }
      if (this.pointsLoading) return
      const pointPackage = this.selectedPointPackage
      this.pointsLoading = true
      try {
        const order = await apiRequest('/api/payments/wechat/points/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json;charset=UTF-8' },
          body: JSON.stringify({ packageId: pointPackage.id })
        })
        await this.showWechatPaymentOrder(order)
      } catch (error) {
        this.showToast(error.message || '微信支付订单创建失败')
      } finally {
        this.pointsLoading = false
      }
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap');

:root {
  --ink: #17211c;
  --muted: #778079;
  --line: #e4e8e4;
  --soft: #f5f7f4;
  --green: #2563eb;
  --green-dark: #1d4ed8;
  --green-light: #eff6ff;
  --page-gutter: clamp(0.75rem, 2.2vw, 2.125rem);
  --section-gap: clamp(1rem, 2vw, 2rem);
  --touch-target: 2.75rem;
}

.customer-service-fab {
  width: 58px;
  height: 58px;
  position: fixed;
  z-index: 1000010;
  right: calc(22px + env(safe-area-inset-right));
  top: 50%;
  bottom: auto;
  display: flex;
  place-items: center;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 2px;
  border: 0;
  border-radius: 18px;
  padding: 0;
  background: #2563eb;
  color: #fff;
  box-shadow: 0 12px 30px rgba(37, 99, 235, .3);
  cursor: pointer;
  touch-action: manipulation;
  transform: translateY(-50%);
  transition: transform .18s ease, background-color .18s ease, box-shadow .18s ease;
}
.customer-service-fab:hover {
  background: #1d4ed8;
  box-shadow: 0 15px 34px rgba(37, 99, 235, .38);
  transform: translateY(calc(-50% - 2px));
}
.customer-service-fab:active { background: #1e40af; transform: translateY(-50%); }
.customer-service-fab.is-open { background: #1d4ed8; }
.customer-service-fab:focus-visible {
  outline: 3px solid rgba(37, 99, 235, .28);
  outline-offset: 3px;
}
.customer-service-fab svg {
  width: 24px;
  height: 24px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}
.customer-service-label {
  color: inherit;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: .04em;
}
.customer-service-status {
  width: 10px;
  height: 10px;
  position: absolute;
  top: 2px;
  right: 2px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #22c55e;
}
.customer-service-fab.is-loading .customer-service-status {
  background: #f59e0b;
  animation: customerServicePulse 1s ease-in-out infinite;
}

/* 使用统一客服入口，避免 53 客服脚本自身的悬浮图标重复显示。 */
#mobile_icon_div { display: none !important; }

@keyframes customerServicePulse {
  50% { opacity: .55; transform: scale(.8); }
}

.customer-service-panel {
  width: min(400px, calc(100vw - 32px));
  height: min(640px, calc(100vh - 48px));
  position: fixed;
  z-index: 1000009;
  right: 24px;
  bottom: 94px;
  overflow: hidden;
  display: grid;
  grid-template-rows: 64px minmax(0, 1fr);
  border: 1px solid #dce5ff;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 22px 60px rgba(22, 42, 82, .24);
}
.customer-service-panel > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px 0 16px;
  background: linear-gradient(135deg, #2f6feb, #2458c9);
  color: #fff;
}
.customer-service-panel > header > div {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.customer-service-avatar {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: rgba(255,255,255,.16);
}
.customer-service-avatar svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}
.customer-service-panel > header span:last-child {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.customer-service-panel > header strong { font-size: 15px; }
.customer-service-panel > header small { color: rgba(255,255,255,.72); font-size: 11px; }
.customer-service-panel > header button {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: background-color .18s ease;
}
.customer-service-panel > header button:hover,
.customer-service-panel > header button:focus-visible { background: rgba(255,255,255,.14); }
.customer-service-panel > iframe {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}
.customer-service-panel-enter-active,
.customer-service-panel-leave-active { transition: opacity .2s ease, transform .2s ease; }
.customer-service-panel-enter,
.customer-service-panel-leave-to { opacity: 0; transform: translateY(10px) scale(.98); }

.creation-date {
  color: #46534b;
  font-size: calc(1em + 2px);
  font-weight: 500;
}

html { font-size: 16px; -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
* { box-sizing: border-box; }
body { min-width: 0; margin: 0; overflow-x: hidden; background: #f3f5f2; color: var(--ink); font-family: Manrope, "PingFang SC", "Microsoft YaHei", sans-serif; }
body.viewer-open { overflow: hidden; }
body.case-detail-open { overflow: hidden; }
body.mobile-nav-open { overflow: hidden; }
button, textarea { font: inherit; }
button { cursor: pointer; }
button, a { -webkit-tap-highlight-color: transparent; touch-action: manipulation; }

#app { width: 100%; min-height: 100vh; min-height: 100dvh; display: flex; }
.app-skip-link { position: fixed; z-index: 50; top: 10px; left: 12px; transform: translateY(-160%); border-radius: 7px; padding: 10px 14px; background: #1d4ed8; color: #fff; font-size: 13px; font-weight: 700; text-decoration: none; transition: transform .15s ease; }
.app-skip-link:focus { transform: translateY(0); }
.mobile-nav-toggle, .mobile-nav-backdrop, .mobile-nav-close { display: none; }
.compact-account { display: none !important; }
.sidebar { position: fixed; z-index: 10; inset: 0 auto 0 0; width: 260px; background: #fff; border-right: 1px solid #e8ebe9; display: flex; align-items: stretch; flex-direction: column; padding: 24px 18px; }
.brand-lockup { min-width: 0; display: flex; align-items: center; gap: 11px; padding: 0 4px 21px; border-bottom: 1px solid #f0f2f1; }
.brand-mark { width: 44px; height: 44px; flex: 0 0 44px; overflow: hidden; border: 1px solid #e6ded8; border-radius: 10px; background: #bd4b34; }
.brand-mark img { width: 100%; height: 100%; display: block; object-fit: cover; }
.brand-copy { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.brand-copy strong { color: #1f2b25; font-size: 16px; line-height: 1.2; letter-spacing: -.2px; white-space: nowrap; }
.brand-copy span { margin-top: 4px; color: #929a95; font-size: 11px; line-height: 1.2; white-space: nowrap; }
.side-nav { width: 100%; display: flex; flex-direction: column; gap: 7px; margin-top: 38px; }
.grouped-nav { min-height: 0; flex: 1; gap: 10px; margin-top: 22px; padding: 0 0 22px; overflow-y: auto; scrollbar-width: none; }
.grouped-nav::-webkit-scrollbar { display: none; }
.home-nav-link { width: 100%; min-height: 52px; display: flex; align-items: center; gap: 13px; border-radius: 9px; padding: 0 13px; color: #5f6a64; text-decoration: none; transition: background .18s ease, color .18s ease; }
.home-nav-link:hover { background: #f5f7f6; color: #29342e; }
.home-nav-link:focus-visible { outline: 2px solid rgba(37,99,235,.28); outline-offset: 2px; }
.home-nav-link.active { background: #f1f5ff; color: var(--green); }
.home-nav-link.active .nav-group-icon { background: #e3ecff; color: var(--green); }
.nav-update-badge { flex: 0 0 auto; border-radius: 999px; padding: 3px 6px; background: #e9f7ee; color: #21834a; font-size: 8px; font-weight: 800; letter-spacing: .05em; }
.update-nav-link.active .nav-update-badge { background: #dbe8ff; color: #1d4ed8; }
.nav-group { width: 100%; border-radius: 8px; }
.nav-group-trigger { width: 100%; height: 52px; display: flex; align-items: center; gap: 13px; border: 0; border-radius: 9px; padding: 0 13px; background: transparent; color: #5f6a64; text-align: left; text-decoration: none; transition: background .18s ease, color .18s ease; }
.nav-group-trigger:hover { background: #f5f7f6; color: #29342e; }
.nav-group.expanded > .nav-group-trigger { color: #2f3b35; }
.nav-group.active > .nav-group-trigger { background: #f1f5ff; color: var(--green); }
.nav-group-icon { width: 27px; height: 27px; flex: 0 0 27px; display: grid; place-items: center; border-radius: 7px; background: transparent; color: #77817c; font-size: 17px; transition: background .18s ease, color .18s ease; }
.nav-group.active .nav-group-icon { background: #e3ecff; color: var(--green); }
.nav-group-label { min-width: 0; flex: 1; font-size: 15px; font-weight: 650; line-height: 1.2; letter-spacing: .05px; }
.nav-group-arrow { flex: 0 0 auto; color: #a1a9a5; font-size: 11px; transition: transform .2s ease; }
.nav-group.expanded .nav-group-arrow { color: #747f79; transform: rotate(180deg); }
.nav-submenu { display: grid; gap: 5px; margin: 5px 0 13px 40px; padding: 0; }
.nav-subitem { min-height: 44px; display: flex; align-items: center; gap: 10px; position: relative; border-radius: 8px; padding: 9px 12px; color: #707b75; text-decoration: none; transition: background .18s ease, color .18s ease, box-shadow .18s ease; }
.nav-subitem:hover { background: #f5f7f6; color: #303a35; }
.nav-subitem.active { background: #eef4ff; color: var(--green); }
.nav-subitem-label { min-width: 0; flex: 1; font-size: 14px; font-weight: 500; line-height: 1.4; }
.nav-subitem.active .nav-subitem-label { font-weight: 700; }
.nav-subitem.unavailable,
.nav-subitem.unavailable:hover,
.nav-subitem.unavailable.active { background: transparent; color: #707b75; box-shadow: none; }
.nav-subitem.unavailable.active .nav-subitem-label { font-weight: 500; }
.nav-group-trigger:focus-visible, .nav-subitem:focus-visible { outline: 2px solid rgba(37,99,235,.28); outline-offset: 2px; }
.nav-submenu-enter-active, .nav-submenu-leave-active { transition: opacity .18s ease, transform .18s ease; transform-origin: top; }
.nav-submenu-enter, .nav-submenu-leave-to { opacity: 0; transform: translateY(-5px); }
.nav-item { position: relative; width: 100%; height: 44px; flex: 0 0 44px; border: 0; border-radius: 5px; padding: 0 10px; background: transparent; display: flex; align-items: center; justify-content: flex-start; gap: 11px; color: #7b8580; text-decoration: none; transition: color .18s ease, background .18s ease; }
.nav-icon { width: 21px; height: 21px; flex: 0 0 21px; display: grid; place-items: center; color: inherit; }
.nav-icon svg { width: 20px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.nav-label { overflow: hidden; font-size: 13px; font-weight: 500; line-height: 1; text-overflow: ellipsis; white-space: nowrap; }
.nav-item:hover { color: #344139; background: #f5f7f6; }
.nav-item.active { color: var(--green); background: transparent; }
.nav-item.active .nav-label { font-weight: 700; }
.nav-item.active::after { content: ""; position: absolute; right: -14px; top: 10px; width: 3px; height: 24px; border-radius: 3px 0 0 3px; background: var(--green); }
.white-model-nav-icon svg { stroke-width: 1.55; }
.side-bottom { margin-top: auto; }
.sidebar-collapse { width: 100%; min-height: 44px; display: flex; align-items: center; gap: 12px; flex: 0 0 auto; margin-top: auto; border: 0; border-top: 1px solid #eef1ef; padding: 10px 12px 0; background: transparent; color: #7a8580; text-align: left; transition: color .18s ease; }
.sidebar-collapse:hover { color: #29342e; }
.sidebar-collapse i { width: 28px; flex: 0 0 28px; text-align: center; font-size: 17px; }
.sidebar-collapse span { font-size: 12px; font-weight: 700; white-space: nowrap; }

.main-shell { flex: 1; min-width: 0; margin-left: 260px; transition: margin-left .22s ease; }
.main-shell { overflow-x: hidden; }
.main-shell > :not(.topbar) { min-width: 0; max-width: 100%; box-sizing: border-box; }
.main-shell > :is(
  .workspace,
  .sketch-plan-workspace,
  .white-model-workspace,
  .style-replace-workspace,
  .erase-workspace,
  .upscale-workspace,
  .cutout-workspace,
  .ratio-workspace,
  .playground-workspace,
  .object-replace,
  .soft-extract-workspace,
  .project-management,
  .case-library,
  .feedback-page,
  .feedback-detail-page
) {
  width: calc(100% - 68px);
  max-width: none;
  min-width: 0;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
}
.topbar { height: 92px; background: rgba(255,255,255,.9); backdrop-filter: blur(12px); border-bottom: 1px solid var(--line); display: flex; align-items: center; justify-content: space-between; padding: 0 4.5vw; position: sticky; top: 0; z-index: 8; }
.eyebrow { margin: 0 0 3px; color: #8b948e; font-size: 10px; font-weight: 700; letter-spacing: 2px; }
.topbar h1 { margin: 0; font-size: 22px; letter-spacing: -.5px; }
.header-actions { min-width: 0; display: flex; align-items: center; gap: 8px; }
.header-actions > * { flex-shrink: 0; }
.topbar-title { min-width: 0; }
.customer-service-guide {
  min-width: 220px;
  max-width: 420px;
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 10px 0 auto;
  border: 1px solid #d8e5fb;
  border-radius: 10px;
  padding: 4px 5px 4px 7px;
  background: linear-gradient(105deg, #f8faff 0%, #eef5ff 100%);
  color: #62708a;
  box-shadow: 0 4px 12px rgba(52, 94, 170, .06);
  font-size: 12px;
  line-height: 1.35;
}
.customer-service-guide > svg {
  width: 25px;
  height: 25px;
  flex: 0 0 25px;
  border-radius: 7px;
  padding: 5px;
  background: #e2ecff;
  fill: none;
  stroke: #3568ce;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

@media (min-width: 1280px) {
  .sidebar.desktop-collapsed { width: 84px; align-items: center; padding: 18px 14px; overflow: visible; }
  .sidebar.desktop-collapsed .brand-lockup { justify-content: center; }
  .sidebar.desktop-collapsed .brand-copy { display: none; }
  .sidebar.desktop-collapsed .brand-mark { width: 44px; height: 44px; flex-basis: 44px; }
  .sidebar.desktop-collapsed .grouped-nav { overflow: visible; gap: 8px; margin-top: 24px; padding-bottom: 0; }
  .sidebar.desktop-collapsed .home-nav-link,
  .sidebar.desktop-collapsed .nav-group-trigger { width: 54px; height: 54px; justify-content: center; padding: 0; }
  .sidebar.desktop-collapsed .nav-group { position: relative; }
  .sidebar.desktop-collapsed .nav-group-icon { width: 30px; height: 30px; flex-basis: 30px; font-size: 17px; }
  .sidebar.desktop-collapsed .nav-group-label,
  .sidebar.desktop-collapsed .nav-group-arrow { display: none; }
  .sidebar.desktop-collapsed .nav-submenu {
    width: 224px;
    position: absolute;
    z-index: 30;
    top: 0;
    left: 64px;
    gap: 3px;
    display: none !important;
    margin: 0;
    padding: 10px;
    border: 1px solid #e3e9e5;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 16px 36px rgba(34,55,43,.16);
  }
  .sidebar.desktop-collapsed .nav-group:hover .nav-submenu,
  .sidebar.desktop-collapsed .nav-group:focus-within .nav-submenu { display: grid !important; }
  .sidebar.desktop-collapsed .sidebar-collapse { width: 54px; justify-content: center; padding-inline: 0; }
  .sidebar.desktop-collapsed .sidebar-collapse span { display: none; }
  .main-shell.nav-collapsed { margin-left: 84px; }
}
.customer-service-guide > span { min-width: 0; flex: 1; white-space: nowrap; }
.customer-service-guide-action {
  min-height: 24px;
  flex: 0 0 auto;
  border: 0;
  border-radius: 6px;
  padding: 0 8px;
  background: #2563eb;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  transition: background-color .18s ease, box-shadow .18s ease;
}
.customer-service-guide-action:hover { background: #1d4ed8; box-shadow: 0 5px 14px rgba(37,99,235,.2); }
.customer-service-guide-action:active { background: #1e40af; }
.customer-service-guide-action:focus-visible,
.customer-service-guide-close:focus-visible { outline: 3px solid rgba(37,99,235,.22); outline-offset: 2px; }
.customer-service-guide-close {
  width: 28px;
  height: 28px;
  min-height: 28px;
  display: grid;
  flex: 0 0 28px;
  place-items: center;
  border: 0;
  border-radius: 7px;
  padding: 0;
  background: transparent;
  color: #8793a8;
  font-size: 18px;
  line-height: 1;
  transition: background-color .18s ease, color .18s ease;
}
.customer-service-guide-close:hover { background: rgba(75, 105, 158, .1); color: #52647f; }
.customer-guide-enter-active,
.customer-guide-leave-active { transition: opacity .2s ease, transform .2s ease; }
.customer-guide-enter,
.customer-guide-leave-to { opacity: 0; transform: translateY(-5px); }
.workflow-entry, .pricing-entry, .membership-pricing-entry, .earn-points, .language-entry, .member-shortcuts { height: 38px; border: 0; border-radius: 8px; }
.workflow-entry { display: inline-flex; align-items: center; gap: 6px; padding: 0 12px; border: 1px solid #bfd2fa; background: #edf4ff; color: #315db7; font-size: 12px; font-weight: 700; cursor: pointer; transition: border-color .18s ease, background-color .18s ease, color .18s ease, transform .18s ease; }
.workflow-entry svg { width: 17px; height: 17px; flex: 0 0 17px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 1.7; }
.workflow-entry:hover { border-color: #8eadef; background: #e1ecff; color: #214b9c; transform: translateY(-1px); }
.workflow-entry:active { background: #d6e5ff; color: #193f88; transform: translateY(0); }
.workflow-entry:focus-visible { outline: 3px solid rgba(37,99,235,.22); outline-offset: 2px; }
.pricing-entry { display: inline-flex; align-items: center; gap: 6px; padding: 0 11px; border: 1px solid #d5deeb; background: #f9fbff; color: #465772; font-size: 12px; font-weight: 700; cursor: pointer; transition: border-color .18s ease, background-color .18s ease, color .18s ease; }
.pricing-entry svg { width: 17px; height: 17px; fill: none; stroke: #5575b8; stroke-linecap: round; stroke-linejoin: round; stroke-width: 1.7; }
.pricing-entry:hover { border-color: #9fb4df; background: #edf3ff; color: #315db7; }
.pricing-entry:focus-visible { outline: 3px solid rgba(37,99,235,.22); outline-offset: 2px; }
.membership-pricing-entry { display: inline-flex; align-items: center; gap: 7px; padding: 0 13px 0 7px; border: 1px solid #ebc778; background: linear-gradient(110deg, #fff8e8, #ffedbd); color: #78450b; font-size: 13px; font-weight: 700; white-space: nowrap; cursor: pointer; box-shadow: 0 5px 14px rgba(176, 111, 22, .12); transition: border-color .18s ease, background .18s ease, color .18s ease, transform .18s ease, box-shadow .18s ease; touch-action: manipulation; }
.membership-pricing-entry:hover { border-color: #d9a741; background: linear-gradient(110deg, #fff3d4, #ffe39a); color: #653603; transform: translateY(-1px); box-shadow: 0 7px 18px rgba(176, 111, 22, .18); }
.membership-pricing-entry:active { transform: translateY(0); }
.membership-pricing-entry:focus-visible { outline: 3px solid rgba(218, 160, 61, .28); outline-offset: 2px; }
.earn-points { display: inline-flex; align-items: center; gap: 5px; padding: 0 10px; background: #eef0ff; color: #5f63df; font-size: 12px; font-weight: 700; box-shadow: inset 0 0 0 1px rgba(95,99,223,.04); transition: transform .18s ease, background .18s ease; }
.earn-points svg { width: 18px; height: 18px; fill: none; stroke: #777bf0; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.earn-points:hover { background: #e5e7ff; transform: translateY(-1px); }
.member-shortcuts { display: flex; overflow: hidden; background: #eef0ff; box-shadow: 0 7px 18px rgba(61,71,132,.08); }
.credits { height: 38px; display: inline-flex; align-items: center; gap: 5px; border: 0; padding: 0 9px; background: #eef0ff; color: #665ee5; transition: background .18s ease; }
.credits:hover { background: #e4e7ff; }
.credits strong { font-size: 14px; font-weight: 700; font-variant-numeric: tabular-nums; }
.points-coin { width: 25px; height: 25px; display: grid; place-items: center; border: 2px solid #fff; border-radius: 50%; background: linear-gradient(145deg, #ffb765, #ff8f50); color: #fff; box-shadow: 0 3px 8px rgba(244,133,66,.25); }
.points-coin svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; }
.vip-badge { width: 26px; height: 26px; display: grid; place-items: center; border-radius: 7px; background: linear-gradient(145deg, #f2b14f, #cf7418); color: #fff; box-shadow: 0 3px 8px rgba(191, 100, 12, .2); }
.vip-badge svg { width: 17px; height: 17px; fill: rgba(255, 255, 255, .3); stroke: currentColor; stroke-width: 1.55; stroke-linecap: round; stroke-linejoin: round; }
.language-dropdown { flex: 0 0 auto; }
.user-session { height: 40px; display: flex; align-items: center; gap: 8px; padding: 0 10px 0 13px; border: 1px solid #e1e6ef; border-radius: 10px; background: #fff; color: #596579; font-size: 12px; }
.user-session span { max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-session button { padding: 3px 0 3px 9px; border: 0; border-left: 1px solid #e3e8f0; background: transparent; color: #476ed6; font-size: 11px; cursor: pointer; }
.language-entry { min-width: 82px; display: inline-flex; align-items: center; justify-content: space-between; gap: 11px; padding: 0 9px 0 12px; background: #f0f2f7; color: #566074; font-size: 13px; transition: background .18s ease; }
.language-entry:hover { background: #e8ebf2; }
.language-entry svg { width: 17px; height: 17px; fill: none; stroke: #9ba2b0; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.language-menu { border-radius: 9px; }
.compact-account-entry { min-width: 44px; height: 44px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: 1px solid #e1e6ef; border-radius: 10px; padding: 0 11px; background: #fff; color: #536074; }
.compact-account-entry i { font-size: 18px; }
.compact-account-entry span { max-width: 92px; overflow: hidden; font-size: 12px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.account-menu { min-width: 160px; }

.vip-dialog { max-height: 90vh; margin-top: 5vh !important; border-radius: 12px; overflow-x: hidden; overflow-y: auto; box-shadow: 0 28px 80px rgba(15,23,42,.28); }
.vip-dialog .el-dialog__header { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; }
.vip-dialog .el-dialog__body { padding: 0; color: #26322b; }
.wechat-pay-dialog { border-radius: 8px; overflow: hidden; }
.wechat-pay-dialog .el-dialog__header { border-bottom: 1px solid #edf0ed; padding: 16px 20px 13px; }
.wechat-pay-dialog .el-dialog__title { color: #17211c; font-size: 16px; font-weight: 700; }
.wechat-pay-dialog .el-dialog__body { padding: 22px 24px 26px; }
.payment-method-icon { width: 20px; height: 20px; display: inline-block; margin-right: 7px; object-fit: contain; vertical-align: middle; }
.payment-dialog-title { display: inline-flex; align-items: center; gap: 8px; }
.payment-dialog-title img { width: 24px; height: 24px; display: block; object-fit: contain; }
.wechat-pay-content { display: flex; flex-direction: column; align-items: center; text-align: center; }
.wechat-pay-mark { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 50%; background: #e8f8ee; color: #18a957; font-size: 22px; }
.wechat-pay-mark img { width: 34px; height: 34px; display: block; object-fit: contain; }
.wechat-pay-content h3 { margin: 12px 0 5px; color: #17211c; font-size: 16px; }
.wechat-pay-content p { margin: 0; color: #778079; font-size: 13px; }
.wechat-pay-content p strong { color: #e11d48; font-size: 18px; }
.wechat-pay-qr { width: 232px; height: 232px; display: grid; place-items: center; margin: 18px 0 12px; border: 1px solid #e2e7e3; border-radius: 6px; background: #fff; }
.wechat-pay-qr img { width: 220px; height: 220px; display: block; }
.wechat-pay-qr > i { color: #18a957; font-size: 28px; }
.wechat-pay-status { color: #647067; font-size: 13px; font-weight: 600; }
.wechat-pay-status.success { color: #168447; }
.wechat-pay-content > small { margin-top: 5px; color: #9aa29c; font-size: 11px; }
.purchase-center { background: #fff; }
.purchase-header { min-height: 72px; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px 20px; border-bottom: 1px solid #e7eaf0; background: #f8fafc; }
.purchase-brand { min-width: 0; display: flex; align-items: center; gap: 13px; }
.purchase-crown { width: 42px; height: 42px; flex: 0 0 42px; display: grid; place-items: center; border: 1px solid #cbd5e1; border-radius: 9px; background: #0f172a; }
.purchase-crown svg { width: 22px; fill: #fb7185; stroke: #fff; stroke-width: 1.3; stroke-linecap: round; stroke-linejoin: round; }
.purchase-brand h2 { margin: 0; color: #202b25; font-size: 19px; }
.purchase-brand p { margin: 4px 0 0; color: #818b84; font-size: 11px; }
.purchase-close { width: 34px; height: 34px; flex: 0 0 34px; border: 0; border-radius: 4px; background: transparent; color: #8b948e; font-size: 23px; line-height: 1; }
.purchase-close:hover { background: #edf0ed; color: #26322b; }
.purchase-steps { display: grid; grid-template-columns: repeat(3,1fr); padding: 0 24px; border-bottom: 1px solid #ecefec; background: #fff; }
.purchase-steps > button { min-width: 0; min-height: 72px; display: flex; align-items: center; gap: 10px; position: relative; border: 0; border-bottom: 2px solid transparent; background: transparent; color: #8a938d; text-align: left; }
.purchase-steps > button.active { border-bottom-color: #e11d48; color: #26322b; }
.step-number { width: 25px; height: 25px; flex: 0 0 25px; display: grid; place-items: center; border-radius: 50%; background: #eef1ee; color: #7e8881; font-size: 10px; font-weight: 700; }
.purchase-steps > button.active .step-number { background: #0f172a; color: #fff; }
.step-copy { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.step-copy strong { font-size: 12px; }
.step-copy small { color: #939c96; font-size: 9px; }
.step-status { margin-left: auto; border-radius: 3px; padding: 3px 6px; background: #e9f7ee; color: #21834a; font-size: 9px; font-weight: 700; }
.step-lock { width: 16px; margin-left: auto; fill: none; stroke: #9ca49f; stroke-width: 1.7; }
.purchase-panel { padding: 22px 24px 24px; }
.points-history-summary{min-height:70px;display:flex;align-items:center;justify-content:space-between;border:1px solid #e2e7e3;border-radius:5px;padding:12px 16px;background:#f7faf8}.points-history-summary div{display:flex;flex-direction:column;gap:4px}.points-history-summary span{color:#7c8780;font-size:11px}.points-history-summary strong{color:#26322b;font-size:24px}.points-history-summary button{border:0;border-radius:4px;padding:8px 14px;background:#2563eb;color:#fff;font-size:11px;cursor:pointer}.points-history-list{max-height:390px;margin-top:14px;overflow-y:auto;border:1px solid #e1e6e2;border-radius:5px}.points-history-list article{min-height:62px;display:flex;align-items:center;gap:11px;padding:9px 13px;border-bottom:1px solid #e7ebe8}.points-history-list article:last-child{border-bottom:0}.history-icon{width:28px;height:28px;display:grid;flex:0 0 28px;place-items:center;border-radius:50%;background:#e8f6ed;color:#23804b}.history-icon.expense{background:#fff0ec;color:#c45b39}.points-history-list article>div{min-width:0;display:flex;flex:1;flex-direction:column;gap:4px}.points-history-list article strong{overflow:hidden;color:#36433b;font-size:12px;text-overflow:ellipsis;white-space:nowrap}.points-history-list article small{color:#939d97;font-size:10px}.points-history-list article>b{color:#27804c;font-size:13px}.points-history-list article>b.expense{color:#c45b39}.points-history-empty{min-height:220px;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:8px;color:#919b95}.points-history-empty i{font-size:24px}
.limited-offer { min-height: 40px; display: flex; align-items: center; gap: 8px; margin-bottom: 10px; padding: 9px 12px; border: 1px solid #fecdd3; border-radius: 7px; background: linear-gradient(100deg, #fff1f2, #ffe4e6); color: #9f1239; font-size: 10px; box-shadow: 0 5px 16px rgba(225,29,72,.09); }
.limited-offer strong { font-size: 11px; }
.membership-consult-compact { min-height: 30px; display: inline-flex; flex: 0 0 auto; align-items: center; gap: 5px; margin-left: auto; border: 1px solid #fda4af; border-radius: 999px; padding: 0 10px; background: #fff; color: #9f1239; font-size: 9px; font-weight: 700; cursor: pointer; transition: background .2s ease, border-color .2s ease; }
.membership-consult-compact:hover { border-color: #e11d48; background: #fff1f2; }
.membership-consult-compact:focus-visible { outline: 3px solid rgba(225,29,72,.2); outline-offset: 2px; }
.membership-consult-compact svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.current-membership-strip { display: flex; align-items: center; gap: 9px; margin-bottom: 12px; border: 1px solid #bbf7d0; border-radius: 7px; padding: 10px 12px; background: #f0fdf4; }
.current-membership-strip span { border-radius: 4px; padding: 4px 7px; background: #15803d; color: #fff; font-size: 9px; font-weight: 700; }
.current-membership-strip strong { color: #14532d; font-size: 13px; }
.current-membership-strip small { margin-left: auto; color: #4d7c5b; font-size: 9px; }
.membership-plan-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-bottom: 14px; }
.membership-plan-card { min-width: 0; min-height: 364px; position: relative; display: flex; flex-direction: column; align-items: flex-start; border: 1px solid #d7dde7; border-radius: 11px; padding: 20px; overflow: hidden; background: #fff; color: #1e293b; text-align: left; cursor: pointer; transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease; }
.membership-plan-card:hover:not(:disabled) { border-color: #94a3b8; transform: translateY(-2px); box-shadow: 0 12px 28px rgba(15,23,42,.1); }
.membership-plan-card:focus-visible { outline: 3px solid rgba(37,99,235,.28); outline-offset: 2px; }
.membership-plan-card.selected { border: 2px solid #fb7185; padding: 19px; background: linear-gradient(155deg, #172033, #0f172a); color: #fff; box-shadow: 0 14px 34px rgba(15,23,42,.25); }
.membership-plan-card.enterprise { border-color: #fdba74; background: linear-gradient(155deg, #fffaf4, #fff7ed); color: #1e293b; }
.membership-plan-card.enterprise.selected { border-color: #fb7185; background: linear-gradient(155deg, #172033, #0f172a); color: #fff; box-shadow: 0 14px 34px rgba(15,23,42,.25); }
.membership-plan-card.personal.current.selected { border-color: #22c55e; background: linear-gradient(155deg,#f0fdf4,#fff); color: #14532d; box-shadow: inset 0 0 0 1px #22c55e, 0 10px 24px rgba(34,197,94,.1); }
.membership-plan-card.personal.current.selected .plan-card-label { background: #dcfce7; color: #166534; }
.membership-plan-card.personal.current.selected > p,
.membership-plan-card.personal.current.selected li { color: #4b6353; }
.membership-plan-card.personal.current.selected li::before { color: #16a34a; }
.membership-plan-card.personal.current.selected .plan-card-price { color: #15803d; }
.membership-plan-card.personal.current.selected .plan-select-state { border-color: #16a34a; background: #16a34a; color: #fff; }
.membership-plan-card.enterprise.upgrade:not(.selected) { border-color: #fb923c; box-shadow: 0 10px 24px rgba(249,115,22,.12); }
.membership-plan-card.current { box-shadow: inset 0 0 0 2px #22c55e; }
.membership-plan-card:disabled { cursor: not-allowed; opacity: .56; }
.plan-card-label { border-radius: 999px; padding: 4px 8px; background: #eef2ff; color: #4338ca; font-size: 9px; font-weight: 700; }
.enterprise .plan-card-label { background: #ffedd5; color: #9a3412; }
.membership-plan-card.selected .plan-card-label { background: rgba(255,255,255,.12); color: #f8fafc; }
.best-value-tag { position: absolute; top: 0; right: 0; border-radius: 0 10px 0 10px; padding: 7px 11px; background: linear-gradient(100deg, #fb7185, #e11d48); color: #fff; font-size: 9px; font-weight: 800; letter-spacing: .04em; }
.membership-plan-card h3 { margin: 16px 0 4px; color: inherit; font-size: 20px; }
.membership-plan-card > p { min-height: 32px; margin: 0; color: #64748b; font-size: 10px; line-height: 1.55; }
.membership-plan-card.enterprise > p { color: #64748b; }
.membership-plan-card.selected > p { color: #cbd5e1; }
.plan-card-price { display: flex; align-items: baseline; margin: 15px 0 14px; color: #be123c; }
.enterprise .plan-card-price, .membership-plan-card.selected .plan-card-price { color: #fb7185; }
.plan-card-price span { margin-right: 2px; font-size: 14px; font-weight: 700; }
.plan-card-price strong { font-size: 36px; line-height: 1; letter-spacing: -.04em; }
.plan-card-price small { margin-left: 5px; color: #64748b; font-size: 10px; }
.enterprise-value { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: -4px 0 14px; border: 1px solid #fed7aa; border-radius: 6px; padding: 8px 9px; background: #fff7ed; }
.enterprise-value strong { color: #c2410c; font-size: 10px; }
.enterprise-value span { color: #9a3412; font-size: 8px; }
.membership-plan-card.selected .enterprise-value { border-color: rgba(251,113,133,.38); background: rgba(225,29,72,.12); }
.membership-plan-card.selected .enterprise-value strong { color: #fda4af; }
.membership-plan-card.selected .enterprise-value span { color: #fecdd3; }
.membership-plan-card ul { display: flex; flex: 1; flex-direction: column; gap: 9px; margin: 0 0 16px; padding: 0; list-style: none; }
.membership-plan-card li { position: relative; padding-left: 17px; color: #475569; font-size: 10px; line-height: 1.45; }
.membership-plan-card li strong { font-weight: 800; }
.membership-plan-card li::before { content: '✓'; position: absolute; left: 0; color: #16a34a; font-weight: 900; }
.membership-plan-card li.muted { color: #94a3b8; }
.membership-plan-card li.muted::before { content: '—'; color: #94a3b8; }
.enterprise li { color: #475569; }
.enterprise li::before { color: #ea580c; }
.membership-plan-card.selected li { color: #e2e8f0; }
.membership-plan-card.selected li::before { color: #fb7185; }
.membership-plan-card.selected li.muted { color: #94a3b8; }
.plan-select-state { width: 100%; min-height: 36px; display: grid; place-items: center; border: 1px solid #cbd5e1; border-radius: 6px; color: #475569; font-size: 10px; }
.selected .plan-select-state { border-color: #e11d48; background: #e11d48; color: #fff; }
.enterprise .plan-select-state { border-color: #fdba74; color: #9a3412; }
.enterprise.selected .plan-select-state { border-color: #e11d48; background: #e11d48; color: #fff; }
.membership-actions { display: flex; align-items: stretch; gap: 10px; }
.membership-actions .membership-action { min-width: 0; flex: 1 1 auto; }
.offer-countdown { display: flex; align-items: center; gap: 4px; margin-left: auto; }
.offer-countdown small { margin-right: 3px; color: #be123c; font-size: 9px; }
.offer-countdown b { min-width: 27px; padding: 5px 6px; border-radius: 4px; background: linear-gradient(180deg, #be123c, #881337); color: #fff; font-size: 12px; font-variant-numeric: tabular-nums; text-align: center; box-shadow: 0 3px 8px rgba(159,18,57,.24); }
.offer-countdown i { color: #9f1239; font-style: normal; font-weight: 800; }
.offer-pulse { width: 7px; height: 7px; flex: 0 0 7px; border-radius: 50%; background: #e11d48; box-shadow: 0 0 0 4px rgba(225,29,72,.12); animation: offer-pulse 1.8s ease-in-out infinite; }
@keyframes offer-pulse { 50% { box-shadow: 0 0 0 7px rgba(225,29,72,0); } }
.annual-plan { min-height: 124px; display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 22px; border: 1px solid #334155; border-radius: 9px; background: #0f172a; box-shadow: inset 4px 0 0 #fb7185; }
.annual-plan.activated { border-color: #a8d9b9; background: #f2fbf5; box-shadow: inset 4px 0 0 #34a761; }
.plan-kicker { display: inline-block; margin-bottom: 8px; border-radius: 4px; padding: 4px 8px; background: #e11d48; color: #fff; font-size: 9px; font-weight: 700; letter-spacing: .08em; }
.annual-plan.activated .plan-kicker { background: #d9f1e1; color: #207a45; }
.annual-plan h3 { margin: 0; color: #fff; font-size: 19px; }
.annual-plan p { margin: 5px 0 0; color: #94a3b8; font-size: 10px; }
.annual-plan.activated h3 { color: #2c302d; }
.annual-plan.activated p { color: #6b7770; }
.annual-plan.activated .annual-price { color: #207a45; }
.annual-plan.activated .annual-price strong { color: #207a45; text-shadow: none; }
.annual-plan.activated .price-now { animation: none; }
.annual-plan.activated .annual-price small { color: #6b7770; }
.active-validity-summary { display: flex; flex: 0 0 auto; align-items: center; gap: 9px; color: #207a45; text-align: right; }
.active-validity-summary > svg { width: 30px; height: 30px; padding: 6px; border-radius: 50%; background: #d9f1e1; fill: none; stroke: currentColor; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; }
.active-validity-summary span { display: flex; flex-direction: column; gap: 2px; }
.active-validity-summary strong { font-size: 14px; }
.active-validity-summary small { color: #557b62; font-size: 9px; }
.annual-price { flex: 0 0 auto; color: #fff; text-align: right; }
.price-saving { display: flex; align-items: center; justify-content: flex-end; gap: 7px; margin-bottom: 7px; }
.price-saving del { color: #94a3b8; font-size: 10px; }
.price-saving b { border-radius: 4px; padding: 3px 6px; background: #fff1f2; color: #be123c; font-size: 9px; }
.price-now { white-space: nowrap; animation: price-urgency 1.6s ease-in-out infinite; transform-origin: right center; }
.price-now em { margin-right: 6px; color: #fda4af; font-size: 9px; font-style: normal; font-weight: 700; }
.annual-price span { font-size: 15px; font-weight: 700; vertical-align: 9px; }
.annual-price strong { color: #fb7185; font-size: 38px; line-height: 1; text-shadow: 0 0 18px rgba(251,113,133,.2); }
.annual-price small { margin-left: 4px; color: #94a3b8; font-size: 10px; }
.plan-benefits { display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 14px; border: 1px solid #e6eae6; border-radius: 6px; background: #fafbfa; }
.plan-benefits span { min-width: 0; display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 14px 8px; text-align: center; }
.plan-benefits span + span { border-left: 1px solid #e6eae6; }
.plan-benefits b { color: #35413a; font-size: 13px; }
.plan-benefits small { color: #929a95; font-size: 9px; }
.purchase-rule { display: flex; align-items: flex-start; gap: 9px; margin: 14px 0; padding: 11px 13px; border-radius: 5px; background: #f4f6f4; }
.purchase-rule span { flex: 0 0 auto; color: #69736d; font-size: 9px; font-weight: 700; }
.purchase-rule p { margin: 0; color: #818a84; font-size: 10px; line-height: 1.55; }
.vip-dialog .purchase-primary.el-button { width: 100%; height: 44px; border-radius: 5px; font-size: 12px; font-weight: 700; }
.vip-dialog .purchase-primary.el-button--primary { border-color: #2563eb; background: #2563eb; }
.vip-dialog .membership-action.el-button--primary { height: 48px; border-color: #be123c; border-radius: 7px; background: linear-gradient(100deg, #e11d48, #be123c); box-shadow: 0 9px 22px rgba(225,29,72,.28); font-size: 13px; animation: purchase-urgency 1.8s ease-in-out infinite; }
@keyframes price-urgency { 50% { transform: scale(1.035); filter: brightness(1.08); } }
@keyframes purchase-urgency { 50% { box-shadow: 0 10px 28px rgba(225,29,72,.43); } }
.vip-dialog .purchase-primary.el-button--primary:hover { filter: brightness(.94); transform: translateY(-1px); }
.vip-dialog .purchase-primary.el-button--primary:focus { outline: 3px solid rgba(37,99,235,.22); outline-offset: 2px; }
.vip-dialog .purchase-primary.is-disabled { border-color: #d9dfda; background: #edf0ed; color: #929a95; }
.purchase-reassurance { margin: 14px 0 12px; color: #7c879f; font-size: 9px; text-align: center; }
.vip-validity-card { overflow: hidden; border: 1px solid #b9ddc6; border-radius: 7px; background: linear-gradient(145deg,#f8fcf9,#eff9f2); box-shadow: 0 8px 22px rgba(36,108,63,.07); }
.validity-header { min-height: 56px; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 10px 15px; border-bottom: 1px solid #d8ebdf; }
.validity-title { min-width: 0; display: flex; align-items: center; gap: 10px; }
.validity-title > div { display: flex; flex-direction: column; gap: 2px; }
.validity-title strong { color: #214d31; font-size: 13px; }
.validity-title small { color: #6e8c78; font-size: 9px; }
.validity-icon { width: 34px; height: 34px; display: grid; flex: 0 0 34px; place-items: center; border-radius: 6px; background: #dff3e6; color: #207a45; }
.validity-icon svg { width: 19px; height: 19px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.validity-header > b { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 6px; border: 1px solid #b8ddc5; border-radius: 999px; padding: 5px 9px; background: #e7f7ed; color: #207a45; font-size: 9px; }
.validity-header > b i { width: 6px; height: 6px; border-radius: 50%; background: #2ba15b; box-shadow: 0 0 0 3px rgba(43,161,91,.12); }
.validity-period { display: grid; grid-template-columns: minmax(0,1fr) 28px minmax(0,1fr); align-items: center; gap: 10px; padding: 14px 16px 15px; }
.validity-period > div { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.validity-period > div:last-child { text-align: right; }
.validity-period span { color: #789080; font-size: 9px; font-weight: 600; }
.validity-period strong { overflow: hidden; color: #263b2e; font-size: 12px; font-variant-numeric: tabular-nums; text-overflow: ellipsis; white-space: nowrap; }
.validity-period > svg { width: 20px; justify-self: center; fill: none; stroke: #7eaa8d; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
@media (max-width: 540px) {
  .membership-plan-grid { grid-template-columns: 1fr; }
  .membership-plan-card { min-height: 0; }
  .limited-offer { flex-wrap: wrap; }
  .membership-consult-compact { margin-left: 15px; }
  .current-membership-strip { align-items: flex-start; flex-wrap: wrap; }
  .current-membership-strip small { width: 100%; margin-left: 0; }
  .validity-period { grid-template-columns: 1fr; gap: 7px; }
  .validity-period > svg { justify-self: start; margin-left: 4px; transform: rotate(90deg); }
  .validity-period > div:last-child { text-align: left; }
}
@media (prefers-reduced-motion: reduce) {
  .membership-plan-card, .membership-action, .offer-pulse { animation: none !important; transition: none !important; }
}
.continue-points { width: 100%; margin-top: 9px; border: 0; background: transparent; color: #2563eb; font-size: 10px; }
.points-locked, .member-ready { min-height: 76px; display: flex; align-items: center; gap: 13px; margin-bottom: 16px; padding: 14px 16px; border-radius: 7px; }
.points-locked { min-height: 104px; border: 1px solid #334155; border-radius: 9px; background: #0f172a; box-shadow: inset 4px 0 0 #fb7185; }
.locked-icon { width: 44px; height: 44px; flex: 0 0 44px; display: grid; place-items: center; border: 1px solid #475569; border-radius: 9px; background: #1e293b; }
.locked-icon svg { width: 22px; fill: none; stroke: #fb7185; stroke-width: 1.8; }
.points-locked > div:nth-child(2) { min-width: 0; flex: 1; }
.points-locked strong, .member-ready strong { display: block; color: #fff; font-size: 14px; }
.locked-step { display: block; margin-bottom: 4px; color: #fda4af; font-size: 9px; font-weight: 700; letter-spacing: .04em; }
.points-locked p { margin: 5px 0 0; color: #94a3b8; font-size: 10px; line-height: 1.5; }
.points-locked .unlock-vip-button { flex: 0 0 auto; min-height: 38px; border-color: #e11d48; border-radius: 6px; background: #e11d48; box-shadow: 0 7px 18px rgba(225,29,72,.2); font-size: 10px; font-weight: 700; }
.points-locked .unlock-vip-button:hover, .points-locked .unlock-vip-button:focus { border-color: #be123c; background: #be123c; }
.member-ready { border: 1px solid #91d0a8; background: #effaf3; box-shadow: inset 4px 0 0 #299657; }
.member-ready strong { color: #245c37; }
.member-ready > span { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 5px; background: #299657; color: #fff; font-size: 9px; font-weight: 700; }
.member-ready > div { display: flex; flex-direction: column; gap: 3px; }
.member-ready small { color: #557b62; font-size: 10px; }
.points-exchange { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 18px; }
.points-exchange > div { min-height: 70px; display: flex; flex-direction: column; justify-content: center; gap: 4px; padding: 12px 15px; border: 1px solid #e2e8f0; border-radius: 7px; background: #f8fafc; }
.points-exchange span { color: #64748b; font-size: 9px; font-weight: 700; }
.points-exchange strong { color: #0f172a; font-size: 15px; }
.points-exchange small { color: #64748b; font-size: 9px; }
.points-section-title { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 11px; }
.points-section-title > div { display: flex; flex-direction: column; gap: 3px; }
.points-section-title strong { color: #26322b; font-size: 13px; }
.points-section-title small { color: #7b8780; font-size: 9px; }
.points-section-title > span { border: 1px solid #fecdd3; border-radius: 4px; padding: 4px 7px; background: #fff1f2; color: #be123c; font-size: 9px; font-weight: 700; }
.points-package-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.points-package-grid > button { min-width: 0; height: 138px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; position: relative; border: 1px solid #cfd8d1; border-radius: 6px; background: #fff; color: #4b5850; transition: .18s; }
.points-package-grid > button:hover:not(:disabled) { border-color: #fb7185; background: #fff7f8; transform: translateY(-1px); box-shadow: 0 7px 18px rgba(225,29,72,.1); }
.points-package-grid > button.selected { border-color: #e11d48; background: #fff1f2; box-shadow: inset 0 0 0 1px #e11d48, 0 7px 18px rgba(225,29,72,.12); }
.points-package-grid.locked > button { border-color: #e2e8f0; background: #f8fafc; color: #94a3b8; cursor: not-allowed; }
.points-package-grid button > small { color: #5f6b64; font-size: 10px; font-weight: 600; }
.points-package-grid button > strong { color: #202d26; font-size: 20px; }
.points-package-grid button > strong em { margin-left: 2px; font-size: 9px; font-style: normal; font-weight: 500; }
.points-package-grid button > b { color: #be123c; font-size: 13px; }
.points-package-grid button > span:last-child { color: #77827b; font-size: 9px; }
.recommended-tag { position: absolute; top: -1px; right: -1px; border-radius: 0 5px 0 5px; padding: 4px 6px; background: #e11d48; color: #fff !important; font-size: 8px !important; }
.package-selected { position: absolute; top: 8px; left: 8px; width: 19px; height: 19px; display: grid; place-items: center; border-radius: 50%; background: #e11d48; color: #fff !important; font-size: 10px !important; font-weight: 700; }
.points-summary { min-height: 56px; display: flex; align-items: center; gap: 12px; margin: 16px 0 12px; padding: 0 16px; border: 1px solid #334155; border-radius: 7px; background: #0f172a; }
.points-summary span { color: #60708d; font-size: 10px; }
.points-summary strong { color: #fff; font-size: 13px; }
.points-summary b { margin-left: auto; color: #fda4af; font-size: 15px; }
.vip-dialog .points-action.el-button--primary { border-color: #e11d48; background: #e11d48; box-shadow: 0 8px 20px rgba(225,29,72,.18); }

.image-viewer { width: 100vw; height: 100vh; height: 100dvh; position: fixed; z-index: 1000020; inset: 0; overflow: hidden; display: grid; grid-template-columns: minmax(0, 1fr); grid-template-rows: auto minmax(0, 1fr) auto; gap: 10px 12px; box-sizing: border-box; padding: calc(12px + env(safe-area-inset-top)) calc(12px + env(safe-area-inset-right)) calc(12px + env(safe-area-inset-bottom)) calc(12px + env(safe-area-inset-left)); background: rgba(8,13,20,.96); color: #fff; outline: 0; }
.image-viewer.has-navigation { grid-template-columns: 56px minmax(0, 1fr) 56px; }
.viewer-topbar { z-index: 3; grid-column: 1 / -1; grid-row: 1; display: flex; align-items: center; justify-content: space-between; gap: 16px; min-width: 0; min-height: 58px; box-sizing: border-box; border-radius: 7px; padding: 10px 190px 10px 14px; background: rgba(24,32,43,.96); }
.viewer-topbar > div { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.viewer-topbar strong { font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.viewer-topbar span { color: rgba(255,255,255,.55); font-size: 10px; }
.viewer-download { min-width: 112px; height: 40px; position: fixed; z-index: 1000022; top: calc(21px + env(safe-area-inset-top)); right: calc(72px + env(safe-area-inset-right)); display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: 1px solid rgba(255,255,255,.24); border-radius: 7px; padding: 0 12px; background: rgba(37,99,235,.88); color: #fff; box-shadow: 0 6px 18px rgba(0,0,0,.24); font-size: 11px; font-weight: 700; cursor: pointer; }
.viewer-download:hover { background: #2563eb; }
.viewer-download:focus-visible { outline: 3px solid rgba(125,159,230,.8); outline-offset: 2px; }
.viewer-download span { color: #fff; font-size: 10px; }
.viewer-close { width: 44px; height: 44px; position: fixed; z-index: 1000022; top: calc(19px + env(safe-area-inset-top)); right: calc(19px + env(safe-area-inset-right)); display: grid; flex: 0 0 44px; place-items: center; border: 1px solid rgba(255,255,255,.24); border-radius: 7px; padding: 0; background: rgba(255,255,255,.12); color: #fff; box-shadow: 0 6px 18px rgba(0,0,0,.24); font-size: 26px; line-height: 1; cursor: pointer; touch-action: manipulation; }
.viewer-close:hover { background: rgba(255,255,255,.16); }
.viewer-close:focus-visible { outline: 3px solid rgba(125,159,230,.8); outline-offset: 2px; }
.viewer-nav { width: 50px; height: 66px; z-index: 4; align-self: center; justify-self: center; grid-row: 2; border: 1px solid rgba(255,255,255,.18); border-radius: 7px; background: rgba(24,32,43,.96); color: #fff; font-size: 42px; line-height: 1; }
.viewer-nav:hover { background: rgba(37,99,235,.88); }
.viewer-prev { grid-column: 1; }
.viewer-next { grid-column: 3; }
.viewer-stage { width: 100%; height: 100%; min-width: 0; min-height: 0; grid-column: 1 / -1; grid-row: 2; overflow: auto; display: block; padding: 0; overscroll-behavior: contain; cursor: grab; touch-action: none; }
.image-viewer.has-navigation .viewer-stage { grid-column: 2; }
.viewer-stage.dragging { cursor: grabbing; }
.viewer-canvas { min-width: 100%; min-height: 100%; position: relative; transition: width .16s ease, height .16s ease; }
.viewer-canvas img { width: 100%; height: 100%; position: absolute; inset: 0; display: block; object-fit: contain; object-position: center; user-select: none; -webkit-user-drag: none; }
.viewer-toolbar { z-index: 3; justify-self: center; grid-column: 1 / -1; grid-row: 3; display: grid; grid-template-columns: 36px 66px 36px 48px; align-items: center; gap: 6px; padding: 7px; border: 1px solid rgba(255,255,255,.14); border-radius: 5px; background: rgba(20,27,36,.92); box-shadow: 0 10px 30px rgba(0,0,0,.3); }
.viewer-toolbar button { height: 34px; border: 0; border-radius: 3px; background: rgba(255,255,255,.1); color: #fff; font-size: 18px; }
.viewer-toolbar button:hover:not(:disabled) { background: rgba(255,255,255,.18); }
.viewer-toolbar button:disabled { opacity: .35; cursor: default; }
.viewer-toolbar span { text-align: center; color: rgba(255,255,255,.8); font-size: 11px; font-variant-numeric: tabular-nums; }
.viewer-toolbar .viewer-reset { font-size: 10px; }
@media (max-width: 520px) {
  .viewer-topbar { padding-right: 122px; }
  .viewer-download { min-width: 44px; width: 44px; padding: 0; }
  .viewer-download span { display: none; }
}
.viewer-enter-active, .viewer-leave-active { transition: opacity .2s ease; }
.viewer-enter, .viewer-leave-to { opacity: 0; }
@keyframes shimmer { to { transform: translateX(100%); } }
@keyframes statusPulse { 0%,100% { opacity: .4; transform: scale(.85); } 50% { opacity: 1; transform: scale(1.15); } }

/* Header controls are reduced in stages so the page title keeps useful space. */
@media (max-width: 1500px) {
  .customer-service-guide { display: none; }
}

@media (max-width: 1380px) {
  .earn-points { display: none; }
}

@media (max-width: 1320px) {
  .user-session { display: none; }
}

@media (max-width: 1279px) {
  .sidebar-collapse { display: none; }
  .customer-service-guide { display: none; }
  .earn-points { display: none; }
  .user-session { display: none; }
  .language-dropdown { display: none !important; }
  .compact-account { display: inline-flex !important; flex: 0 0 auto; }
  .main-shell small { font-size: 0.6875rem !important; line-height: 1.45; }
  .main-shell input:not([type="range"]):not([type="file"]),
  .main-shell textarea,
  .main-shell select,
  .main-shell .el-input__inner,
  .main-shell .el-textarea__inner { font-size: 0.875rem !important; }
}

@media (min-width: 768px) and (max-width: 1279px) {
  .sidebar { width: 84px; align-items: center; padding: 18px 14px; overflow: visible; }
  .brand-lockup { justify-content: center; }
  .brand-copy { display: none; }
  .brand-mark { width: 44px; height: 44px; flex-basis: 44px; }
  .side-nav { gap: 8px; margin-top: 24px; }
  .grouped-nav { overflow: visible; padding-bottom: 0; }
  .nav-group { position: relative; }
  .nav-group-trigger { width: 54px; height: 54px; justify-content: center; padding: 0; }
  .nav-group-icon { width: 30px; height: 30px; flex-basis: 30px; font-size: 17px; }
  .nav-group-label, .nav-group-arrow { display: none; }
  .nav-submenu {
    width: 224px;
    position: absolute;
    z-index: 30;
    top: 0;
    left: 64px;
    gap: 3px;
    margin: 0;
    padding: 10px;
    border: 1px solid #e3e9e5;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 16px 36px rgba(34, 55, 43, .16);
    display: none !important;
  }
  .nav-group:hover .nav-submenu,
  .nav-group:focus-within .nav-submenu { display: grid !important; }
  .nav-subitem { min-height: 44px; padding: 8px 10px; }
  .nav-subitem-label { font-size: 14px; }
  .main-shell { margin-left: 84px; }
  .main-shell > :is(
    .workspace,
    .sketch-plan-workspace,
    .white-model-workspace,
    .style-replace-workspace,
    .erase-workspace,
    .upscale-workspace,
    .cutout-workspace,
    .ratio-workspace,
    .playground-workspace,
    .object-replace,
    .soft-extract-workspace,
    .project-management,
    .case-library,
    .referral-page,
    .material-library,
    .community-page,
    .feedback-detail-page
  ) { width: calc(100% - 40px); }
  .topbar { height: 78px; padding: 0 clamp(18px, 3vw, 34px); }
  .topbar h1 { font-size: 20px; }
  .header-actions { gap: 7px; }
  .workflow-entry, .membership-pricing-entry { height: 44px; }
  .member-shortcuts, .credits { height: 44px; }
}

@media (max-width: 900px) and (min-width: 768px) {
  .pricing-entry { width: 44px; height: 44px; justify-content: center; padding: 0; }
  .pricing-entry span { display: none; }
  .compact-account-entry span { display: none; }
}

@media (max-height: 560px) and (min-width: 768px) and (max-width: 1279px) {
  .sidebar { padding-top: 10px; padding-bottom: 10px; }
  .brand-lockup { padding-bottom: 8px; }
  .brand-mark { width: 38px; height: 38px; flex-basis: 38px; }
  .side-nav { gap: 3px; margin-top: 8px; }
  .nav-group-trigger { height: 44px; }
  .nav-group-icon { width: 28px; height: 28px; flex-basis: 28px; }
}

@media (max-width: 767px) {
  .customer-service-fab {
    display: none;
  }
  .customer-service-panel {
    width: calc(100vw - 16px);
    height: calc(100dvh - 16px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
    right: 8px;
    bottom: calc(8px + env(safe-area-inset-bottom));
    grid-template-rows: 60px minmax(0, 1fr);
    border-radius: 16px;
  }
  .mobile-nav-toggle { width: 44px; height: 44px; position: fixed; z-index: 12; top: calc(12px + env(safe-area-inset-top)); left: 12px; display: grid; place-items: center; border: 1px solid #dfe5e0; border-radius: 11px; background: #fff; color: #334039; box-shadow: 0 5px 15px rgba(29,43,35,.08); font-size: 20px; }
  .mobile-nav-backdrop { position: fixed; z-index: 13; inset: 0; display: block; border: 0; padding: 0; background: rgba(18,27,22,.48); backdrop-filter: blur(2px); }
  .sidebar { z-index: 14; width: min(86vw, 300px); padding: calc(16px + env(safe-area-inset-top)) 16px calc(20px + env(safe-area-inset-bottom)); transform: translateX(-105%); transition: transform .22s ease; box-shadow: none; }
  .sidebar.mobile-open { transform: translateX(0); box-shadow: 18px 0 45px rgba(23,35,28,.18); }
  .brand-lockup { min-height: 56px; gap: 10px; padding: 0 2px 16px; }
  .brand-copy { display: flex; }
  .brand-mark { width: 42px; height: 42px; flex-basis: 42px; }
  .mobile-nav-close { width: 44px; height: 44px; margin-left: auto; display: grid; flex: 0 0 44px; place-items: center; border: 0; border-radius: 10px; background: #f2f4f2; color: #59645e; font-size: 19px; }
  .side-nav { gap: 6px; margin-top: 14px; }
  .grouped-nav { overflow-y: auto; }
  .nav-group-trigger { min-height: 50px; }
  .nav-submenu { gap: 3px; margin: 4px 0 10px 39px; }
  .nav-subitem { min-height: 46px; padding: 9px 12px; }
  .nav-subitem-label { font-size: 14px; }
  .main-shell { width: 100%; margin-left: 0; }
  .main-shell > :is(
    .workspace,
    .sketch-plan-workspace,
    .white-model-workspace,
    .style-replace-workspace,
    .erase-workspace,
    .upscale-workspace,
    .cutout-workspace,
    .ratio-workspace,
    .playground-workspace,
    .object-replace,
    .soft-extract-workspace,
    .project-management,
    .case-library,
    .referral-page,
    .material-library,
    .community-page,
    .feedback-detail-page
  ) { width: calc(100% - 24px); }
  .main-shell small { font-size: 0.75rem !important; line-height: 1.45; }
  .main-shell p { line-height: 1.55; }
  .main-shell :is(.home-dashboard, .playground-workspace) button { min-height: var(--touch-target); font-size: clamp(0.75rem, 3.3vw, 0.875rem) !important; }
  .main-shell label { font-size: 0.75rem !important; }
  .main-shell :is(.home-dashboard, .playground-workspace) input:not([type="range"]):not([type="file"]),
  .main-shell :is(.home-dashboard, .playground-workspace) textarea,
  .main-shell :is(.home-dashboard, .playground-workspace) select,
  .main-shell :is(.home-dashboard, .playground-workspace) .el-input__inner,
  .main-shell :is(.home-dashboard, .playground-workspace) .el-textarea__inner { min-height: var(--touch-target); font-size: 1rem !important; }
  .main-shell :is(
    .floorplan-workspace,
    .white-model-workspace,
    .style-replace-workspace,
    .erase-workspace,
    .upscale-workspace,
    .object-replace,
    .soft-extract-workspace,
    .project-management,
    .case-library,
    .referral-page,
    .material-library,
    .community-page,
    .runninghub-page,
    .feature-placeholder
  ) button {
    min-height: var(--touch-target);
    font-size: 13px !important;
    line-height: 1.25;
  }
  .main-shell :is(
    .floorplan-workspace,
    .white-model-workspace,
    .style-replace-workspace,
    .erase-workspace,
    .upscale-workspace,
    .object-replace,
    .soft-extract-workspace,
    .project-management,
    .case-library,
    .referral-page,
    .material-library,
    .community-page,
    .runninghub-page,
    .feature-placeholder
  ) :is(input:not([type="range"]):not([type="file"]), textarea, select, .el-input__inner, .el-textarea__inner) {
    min-height: var(--touch-target);
    font-size: 16px !important;
  }
  .main-shell :is(
    .floorplan-workspace,
    .white-model-workspace,
    .style-replace-workspace,
    .erase-workspace,
    .upscale-workspace,
    .object-replace,
    .soft-extract-workspace,
    .project-management,
    .case-library,
    .referral-page,
    .runninghub-page,
    .feature-placeholder
  ) :is(.generate-button, .generate-report, .upscale-generate, .run-button) {
    height: auto;
    min-height: 46px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .main-shell .floorplan-workspace .magic-button { height: 40px; padding-left: 10px; padding-right: 10px; }
  .main-shell .floorplan-workspace .prompt-suggestions { gap: 7px; }
  .main-shell .floorplan-workspace .prompt-suggestions button { padding: 6px 9px; }
  .main-shell .floorplan-workspace .composer-footer { gap: 12px; }
  .main-shell .upscale-workspace .option-grid button { min-height: 48px; }
  .main-shell .style-replace-workspace :is(.flow-panel, .simple-lighting-control, .zone-lighting-control) { padding: 14px; }
  .main-shell .upscale-workspace :is(.upscale-controls, .upscale-preview, .upscale-history) { padding: 14px; }
  .main-shell .soft-extract-workspace :is(.extract-settings, .method-panel, .extraction-history),
  .main-shell .white-model-workspace :is(.white-model-settings, .method-panel, .white-model-history, .furniture-history-section) { padding: 14px; }
  .main-shell input[type="range"] { min-height: 32px; }
  .main-shell :is(
    .preview-actions,
    .result-actions,
    .comparison-actions,
    .report-actions,
    .stage-result-actions,
    .history-actions
  ) {
    max-width: 100%;
    flex-wrap: wrap;
  }
  .main-shell :is(
    .preview-actions,
    .result-actions,
    .comparison-actions,
    .report-actions,
    .stage-result-actions,
    .history-actions
  ) > * { max-width: 100%; box-sizing: border-box; }
  .topbar { height: calc(68px + env(safe-area-inset-top)); padding: env(safe-area-inset-top) 12px 0 68px; gap: 8px; }
  .eyebrow { display: none; }
  .topbar-title { flex: 1; overflow: hidden; }
  .topbar h1 { overflow: hidden; font-size: clamp(15px, 4.5vw, 18px); line-height: 1.25; text-overflow: ellipsis; white-space: nowrap; }
  .header-actions { flex: 0 0 auto; gap: 6px; }
  .workflow-entry, .membership-pricing-entry { height: 44px; }
  .pricing-entry { width: 44px; height: 44px; justify-content: center; padding: 0; }
  .pricing-entry span { display: none; }
  .language-dropdown, .user-session { display: none; }
  .member-shortcuts, .credits { height: 44px; }
  .member-shortcuts { border-radius: 10px; box-shadow: none; }
  .credits { min-width: 61px; padding: 0 8px; }
  .credits strong { font-size: 13px; }
  .points-coin { width: 26px; height: 26px; }
  .points-coin svg { width: 18px; height: 18px; }
  .compact-account-entry { width: 44px; min-width: 44px; padding: 0; }
  .compact-account-entry span { display: none; }
  .vip-dialog { width: calc(100% - 20px) !important; max-height: 90vh; margin-top: 5vh !important; overflow-y: auto; }
  .purchase-header { min-height: 64px; padding: 10px 14px; }
  .purchase-crown { width: 36px; height: 36px; flex-basis: 36px; }
  .purchase-crown svg { width: 20px; }
  .purchase-brand h2 { font-size: 16px; }
  .purchase-brand p { max-width: 225px; font-size: 12px; line-height: 1.5; }
  .purchase-steps { padding: 0 14px; }
  .purchase-steps > button { min-height: 64px; gap: 8px; }
  .step-number { width: 22px; height: 22px; flex-basis: 22px; }
  .step-copy strong { font-size: 11px; }
  .step-copy small { font-size: 11px; }
  .step-status { display: none; }
  .purchase-panel { padding: 16px; }
  .limited-offer { align-items: flex-start; flex-wrap: wrap; font-size: 11px; line-height: 1.45; }
  .offer-countdown { width: 100%; margin-left: 15px; }
  .annual-plan { min-height: 104px; padding: 18px; }
  .annual-price strong { font-size: 32px; }
  .plan-benefits small { font-size: 11px; }
  .points-locked { align-items: flex-start; flex-wrap: wrap; }
  .points-locked .el-button { margin-left: 45px; }
  .points-exchange { align-items: flex-start; flex-direction: column; gap: 5px; }
  .points-package-grid { grid-template-columns: repeat(2, 1fr); }
  .points-package-grid > button { height: 112px; }
  .image-viewer { gap: 8px; padding: calc(8px + env(safe-area-inset-top)) calc(8px + env(safe-area-inset-right)) calc(8px + env(safe-area-inset-bottom)) calc(8px + env(safe-area-inset-left)); }
  .image-viewer.has-navigation { grid-template-columns: 44px minmax(0, 1fr) 44px; }
  .viewer-nav { width: 42px; height: 56px; font-size: 34px; }
  .viewer-close { top: calc(15px + env(safe-area-inset-top)); right: calc(15px + env(safe-area-inset-right)); }
  .viewer-close, .viewer-toolbar button { min-width: 44px; min-height: 44px; }
  .viewer-toolbar { grid-template-columns: 44px 62px 44px 52px; max-width: 100%; }
  .el-message {
    width: calc(100% - 24px) !important;
    min-width: 0 !important;
    max-width: calc(100% - 24px);
    left: 12px !important;
    box-sizing: border-box;
    transform: none !important;
  }
}

@media (max-width: 700px) {
  .workflow-entry { width: 44px; min-width: 44px; justify-content: center; padding: 0; }
  .workflow-entry span { display: none; }
}

@media (max-width: 520px) {
  .pricing-entry { display: none; }
  .member-shortcuts { display: none; }
  .membership-pricing-entry { width: 44px; min-width: 44px; justify-content: center; padding: 0; }
  .membership-pricing-entry__label { display: none; }
}

@media (max-width: 390px) {
  .topbar { padding-right: 8px; }
  .topbar h1 { font-size: 15px; }
  .credits { min-width: 54px; gap: 3px; padding: 0 6px; }
  .points-coin { width: 24px; height: 24px; }
  .purchase-steps { grid-template-columns: 1fr; }
  .purchase-steps > button { min-height: 52px; }
  .annual-plan { align-items: flex-start; flex-direction: column; }
  .annual-price { width: 100%; text-align: left; }
  .price-saving { justify-content: flex-start; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { scroll-behavior: auto !important; animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; }
}
.points-insufficient-alert{border-radius:6px}.points-insufficient-alert .el-message-box__status{color:#e79624}.points-insufficient-alert .el-message-box__message{color:#4a554e;line-height:1.7}.points-insufficient-alert .el-message-box__btns .el-button--primary{border-color:#2563eb;background:#2563eb}
</style>
